import React from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Button,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Switch,
  FormControlLabel,
  Link,
  CircularProgress,
  LinearProgress,
  Tabs,
  Tab,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  ContentCopy as ContentCopyIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  VpnKey as VpnKeyIcon,
  Download as DownloadIcon,
  Upload as UploadIcon,
  Refresh as RefreshIcon,
  HelpOutline as HelpOutlineIcon,
  Folder as FolderIcon,
  Pause as PauseIcon,
  PlayArrow as PlayArrowIcon,
  Close as CloseIcon,
  ExpandLess as ExpandLessIcon,
} from '@mui/icons-material';
import toast from 'react-hot-toast';
import { AdminKeyService } from '../../services/adminKeyService';
import { configureSupabase } from '../../lib/supabase';

type ProviderId = 'mistral' | 'groq' | 'openai' | 'anthropic';

type ApiKeyRecord = {
  id: string;
  name: string;
  key: string;
  createdAt?: string;
  updatedAt?: string;
};

const formatBytes = (bytes: number) => {
  const n = Number(bytes || 0);
  if (!Number.isFinite(n) || n <= 0) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  let v = n;
  let i = 0;
  while (v >= 1024 && i < units.length - 1) {
    v /= 1024;
    i += 1;
  }
  const digits = i === 0 ? 0 : i <= 2 ? 1 : 2;
  return `${v.toFixed(digits)} ${units[i]}`;
};

type SupabaseConnection = {
  url: string;
  anonKey: string;
  hasServiceRole?: boolean;
  createdAt?: string;
  updatedAt?: string;
} | null;

type SupabaseServiceRoleConfig = {
  hasServiceRole: boolean;
  createdAt?: string;
  updatedAt?: string;
} | null;

type SupabasePostgresConfig = {
  host: string;
  port: number;
  database: string;
  user: string;
  sslmode: string;
  hasPassword: boolean;
  createdAt?: string;
  updatedAt?: string;
} | null;

type ParsedPgUri = {
  host: string;
  port: number;
  database: string;
  user: string;
  password?: string;
  sslmode?: string;
} | null;

type PgHelpDialogState = {
  open: boolean;
  title: string;
  message: string;
  rawError?: string;
  steps?: string[];
} | null;

type SupabaseMgmtConfig = {
  projectRef: string;
  hasToken: boolean;
  createdAt?: string;
  updatedAt?: string;
} | null;

type StorageBackupJobBucket = {
  id: string;
  name: string;
  total: number;
  done: number;
  offset: number;
};

type StorageBackupJob = {
  jobId: string;
  status: 'running' | 'paused' | 'done' | 'canceled' | 'error';
  message?: string | null;
  backupDir: string;
  useServiceRole: boolean;
  buckets: StorageBackupJobBucket[];
  bucketIndex: number;
  totalBuckets: number;
  totalObjects: number;
  written: number;
  failed: number;
  failures?: string[];
  createdAt?: string;
  updatedAt?: string;
} | null;

type SupabaseOverviewTable = {
  schema: string;
  name: string;
  kind: string;
  approxRows: number;
  totalBytes: number;
};

type SupabaseOverviewBucket = {
  id: string;
  name: string;
  public: boolean;
  created_at?: string | null;
  updated_at?: string | null;
};

type SupabaseOverview = {
  tables: SupabaseOverviewTable[];
  tablesError?: string | null;
  buckets?: SupabaseOverviewBucket[];
  bucketsError?: string | null;
  functions: any[] | null;
  functionsError?: string | null;
} | null;

const PROVIDERS: Array<{ id: ProviderId; label: string; description: string }> = [
  { id: 'mistral', label: 'Mistral', description: 'Mistral API (chat/completions)' },
  { id: 'groq', label: 'Groq', description: 'Groq API (LLM inference)' },
  { id: 'openai', label: 'OpenAI', description: 'OpenAI API' },
  { id: 'anthropic', label: 'Anthropic', description: 'Anthropic Claude API' },
];

const API_ENDPOINT = '/api/integrations-keys.php';

const maskKey = (k: string) => {
  const s = String(k || '');
  if (!s) return '';
  if (s.length <= 10) return '•'.repeat(s.length);
  return `${s.slice(0, 4)}••••••••${s.slice(-4)}`;
};

const copyToClipboard = async (text: string) => {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    return false;
  }
};

const extractSupabaseProjectRef = (value: string) => {
  const s = String(value || '').trim();
  if (!s) return '';
  // If user pasted the ref already.
  if (/^[a-z0-9-]{8,64}$/i.test(s) && !s.includes('.')) return s.toLowerCase();
  // If user pasted a full Supabase project URL: https://<ref>.supabase.co
  try {
    const u = new URL(s);
    const host = (u.hostname || '').toLowerCase();
    const m = host.match(/^([a-z0-9-]{8,64})\.supabase\.co$/i);
    if (m?.[1]) return m[1].toLowerCase();
  } catch {
    // ignore
  }
  return '';
};

const buildPostgresHelp = (err: string, currentHost?: string): PgHelpDialogState => {
  const raw = String(err || '').trim();
  const e = raw.toLowerCase();
  const host = String(currentHost || '').trim();

  const base: PgHelpDialogState = {
    open: true,
    title: 'Postgres connection failed',
    message: raw || 'Unknown error',
    rawError: raw,
    steps: [],
  };

  const add = (s: string) => {
    if (!base.steps) base.steps = [];
    base.steps.push(s);
  };

  if (e.includes('driver not installed') || e.includes('could not find driver') || e.includes('pdo pgsql')) {
    base.title = 'Postgres driver missing on server';
    base.message = 'Your PHP runtime does not have the Postgres PDO driver enabled, so the server cannot connect to Postgres.';
    add('Enable the PHP extensions: pdo_pgsql and pgsql (in php.ini).');
    add('Restart the PHP server after editing php.ini.');
    add('Verify with phpinfo() that “PDO drivers” includes “pgsql”.');
    return base;
  }

  if (e.includes('could not translate host name') || e.includes('unknown host') || e.includes('getaddrinfo failed') || e.includes('name or service not known')) {
    base.title = 'Database host not reachable (DNS / IPv4 vs IPv6)';
    base.message = 'Your server cannot resolve or reach the database host. With Supabase, this often happens when using the “Direct connection” host on an IPv4-only network (direct DB host may be IPv6-only).';
    add('In Supabase Dashboard → Project Settings → Database → Connection string: switch “Method” to “Session pooler” (recommended) or “Transaction pooler”.');
    add('Copy the pooler connection string and paste it into the Connection string (URI) field here, then click Parse → Save → Test connection.');
    add('If you must use direct connection, you need IPv6 connectivity or the Supabase IPv4 add-on.');
    if (host) {
      add(`Current host: ${host}`);
    }
    return base;
  }

  if (e.includes('password authentication failed') || e.includes('28p01') || e.includes('authentication failed') || e.includes('invalid password')) {
    base.title = 'Authentication failed (wrong user/password)';
    base.message = 'The database rejected the credentials.';
    add('Double-check the database password in Supabase (Database settings).');
    add('If you used a connection string with “[YOUR-PASSWORD]”, paste the real password when prompted.');
    add('Verify the user is correct (often “postgres”, unless you created a dedicated user).');
    return base;
  }

  if (e.includes('ssl') || e.includes('certificate') || e.includes('tls')) {
    base.title = 'SSL/TLS issue while connecting';
    base.message = 'The server could not establish a secure connection to Postgres.';
    add('Try sslmode=require in the connection string (recommended for Supabase).');
    add('If you are using a custom environment, ensure your system CA certificates are up to date.');
    add('If Supabase requires verification, you may need sslmode=verify-full and proper CA configuration.');
    return base;
  }

  if (e.includes('timeout') || e.includes('could not connect to server') || e.includes('connection refused') || e.includes('08006')) {
    base.title = 'Could not connect to the database server';
    base.message = 'The server could not open a network connection to the database host/port.';
    add('Prefer Supabase Session pooler connection strings for app servers.');
    add('Check that the host and port match the connection string you copied in Supabase.');
    add('If you are on a restricted network, outbound port 5432/6543 may be blocked; try the pooler option.');
    return base;
  }

  base.title = 'Postgres connection failed';
  base.message = raw || 'Unknown error';
  add('Try using the Supabase Session pooler connection string (instead of Direct connection).');
  add('Verify host, port, database, user, password, and sslmode.');
  return base;
};

const parsePostgresConnectionString = (raw: string): ParsedPgUri => {
  const s = String(raw || '').trim();
  if (!s) return null;
  try {
    const u = new URL(s);
    if (!/^postgres(ql)?:$/i.test(u.protocol)) return null;
    const database = decodeURIComponent((u.pathname || '').replace(/^\//, ''));
    if (!u.hostname) return null;
    const out: ParsedPgUri = {
      host: u.hostname,
      port: u.port ? parseInt(u.port, 10) : 5432,
      database,
      user: decodeURIComponent(u.username || ''),
    };
    if (u.password) out.password = decodeURIComponent(u.password);
    const sslmode = u.searchParams.get('sslmode');
    if (sslmode) out.sslmode = sslmode;
    return out;
  } catch {
    return null;
  }
};

const IntegrationsApiKeysTab: React.FC = () => {
  const [loading, setLoading] = React.useState<boolean>(false);
  const [saving, setSaving] = React.useState<boolean>(false);
  const [deleting, setDeleting] = React.useState<boolean>(false);
  const [loadingSupabase, setLoadingSupabase] = React.useState<boolean>(false);
  const [savingSupabase, setSavingSupabase] = React.useState<boolean>(false);
  const [supabaseConn, setSupabaseConn] = React.useState<SupabaseConnection>(null);
  const [supabaseUrl, setSupabaseUrl] = React.useState<string>('');
  const [supabaseAnonKey, setSupabaseAnonKey] = React.useState<string>('');
  const [loadingServiceRole, setLoadingServiceRole] = React.useState<boolean>(false);
  const [savingServiceRole, setSavingServiceRole] = React.useState<boolean>(false);
  const [serviceRoleCfg, setServiceRoleCfg] = React.useState<SupabaseServiceRoleConfig>(null);
  const [serviceRoleKeyInput, setServiceRoleKeyInput] = React.useState<string>('');

  const [loadingPg, setLoadingPg] = React.useState<boolean>(false);
  const [savingPg, setSavingPg] = React.useState<boolean>(false);
  const [testingPg, setTestingPg] = React.useState<boolean>(false);
  const [pgCfg, setPgCfg] = React.useState<SupabasePostgresConfig>(null);
  const [pgHost, setPgHost] = React.useState<string>('');
  const [pgPort, setPgPort] = React.useState<string>('5432');
  const [pgDatabase, setPgDatabase] = React.useState<string>('');
  const [pgUser, setPgUser] = React.useState<string>('');
  const [pgPassword, setPgPassword] = React.useState<string>('');
  const [pgSslMode, setPgSslMode] = React.useState<string>('require');

  const [pgConnectionString, setPgConnectionString] = React.useState<string>('');
  const [pgPasswordPromptOpen, setPgPasswordPromptOpen] = React.useState<boolean>(false);
  const [pgPasswordPromptValue, setPgPasswordPromptValue] = React.useState<string>('');
  const [pgPendingSave, setPgPendingSave] = React.useState<{
    host: string;
    port: number;
    database: string;
    user: string;
    sslmode: string;
  } | null>(null);

  const [pgHelp, setPgHelp] = React.useState<PgHelpDialogState>(null);
  const [mgmtCfg, setMgmtCfg] = React.useState<SupabaseMgmtConfig>(null);
  const [loadingMgmt, setLoadingMgmt] = React.useState<boolean>(false);
  const [savingMgmt, setSavingMgmt] = React.useState<boolean>(false);
  const [mgmtProjectRef, setMgmtProjectRef] = React.useState<string>('');
  const [mgmtToken, setMgmtToken] = React.useState<string>('');
  const [mgmtRefAuto, setMgmtRefAuto] = React.useState<boolean>(true);

  const [overviewQuery, setOverviewQuery] = React.useState<string>('');
  const [loadingOverview, setLoadingOverview] = React.useState<boolean>(false);
  const [overview, setOverview] = React.useState<SupabaseOverview>(null);
  const [bucketZipBusy, setBucketZipBusy] = React.useState<Record<string, boolean>>({});
  const [bucketSqlBusy, setBucketSqlBusy] = React.useState<Record<string, boolean>>({});
  const [bucketBackupAllRunning, setBucketBackupAllRunning] = React.useState<boolean>(false);
  const [storageBackupJob, setStorageBackupJob] = React.useState<StorageBackupJob>(null);
  const [storageBackupMinimized, setStorageBackupMinimized] = React.useState<boolean>(false);
  const [storageBackupPos, setStorageBackupPos] = React.useState<{ x: number; y: number } | null>(null);
  const storageBackupTickInFlight = React.useRef<boolean>(false);
  const storageBackupPopupRef = React.useRef<HTMLDivElement | null>(null);
  const storageBackupDragActive = React.useRef<boolean>(false);
  const storageBackupDragOffset = React.useRef<{ dx: number; dy: number }>({ dx: 0, dy: 0 });

  const closeStorageBackupPopup = React.useCallback(() => {
    setStorageBackupJob(null);
    setBucketBackupAllRunning(false);
    storageBackupTickInFlight.current = false;
    storageBackupDragActive.current = false;
  }, []);

  const [tablesSource, setTablesSource] = React.useState<'supabase' | 'backups'>('supabase');
  const [edgeSource, setEdgeSource] = React.useState<'supabase' | 'backups'>('supabase');
  const [tableLocalBackups, setTableLocalBackups] = React.useState<
    Array<{ schema: string; table: string; folderName: string; schemaPath: string; updatedAt?: string | null }>
  >([]);
  const [edgeLocalBackups, setEdgeLocalBackups] = React.useState<
    Array<{ name: string; folder: string; status: string; fileName?: string; updatedAt?: string | null }>
  >([]);
  const [loadingTableBackups, setLoadingTableBackups] = React.useState<boolean>(false);
  const [loadingEdgeBackups, setLoadingEdgeBackups] = React.useState<boolean>(false);
  const [tableBackupsLoaded, setTableBackupsLoaded] = React.useState<boolean>(false);
  const [edgeBackupsLoaded, setEdgeBackupsLoaded] = React.useState<boolean>(false);
  const tableBackupsInFlight = React.useRef<boolean>(false);
  const edgeBackupsInFlight = React.useRef<boolean>(false);
  const [deployingBackupFn, setDeployingBackupFn] = React.useState<Record<string, boolean>>({});
  const [edgeHelpOpen, setEdgeHelpOpen] = React.useState<boolean>(false);
  const [downloadingFn, setDownloadingFn] = React.useState<Record<string, boolean>>({});
  const [deletingFn, setDeletingFn] = React.useState<Record<string, boolean>>({});
  const [deleteDialogOpen, setDeleteDialogOpen] = React.useState<boolean>(false);
  const [deleteTarget, setDeleteTarget] = React.useState<string>('');
  const [deleteConfirm, setDeleteConfirm] = React.useState<string>('');
  const [deleteBackupFirst, setDeleteBackupFirst] = React.useState<boolean>(true);

  const [deleteBackupDialogOpen, setDeleteBackupDialogOpen] = React.useState<boolean>(false);
  const [deleteBackupTarget, setDeleteBackupTarget] = React.useState<string>('');
  const [deleteBackupLoading, setDeleteBackupLoading] = React.useState<boolean>(false);
  const [deleteBackupRunning, setDeleteBackupRunning] = React.useState<boolean>(false);
  const [deleteBackupVariants, setDeleteBackupVariants] = React.useState<
    Array<{ folder: string; n: number; updatedAt?: string | null }>
  >([]);
  const [deleteBackupSelected, setDeleteBackupSelected] = React.useState<Record<string, boolean>>({});

  const [bulkDeleteBackupsOpen, setBulkDeleteBackupsOpen] = React.useState<boolean>(false);
  const [bulkDeleteBackupsLoading, setBulkDeleteBackupsLoading] = React.useState<boolean>(false);
  const [bulkDeleteBackupsRunning, setBulkDeleteBackupsRunning] = React.useState<boolean>(false);
  const [bulkDeleteBackupsData, setBulkDeleteBackupsData] = React.useState<
    Array<{ name: string; variants: Array<{ folder: string; n: number; updatedAt?: string | null }> }>
  >([]);
  const [bulkDeleteBackupsSelected, setBulkDeleteBackupsSelected] = React.useState<Record<string, boolean>>({});
  const [bulkDialogOpen, setBulkDialogOpen] = React.useState<boolean>(false);
  const [bulkMode, setBulkMode] = React.useState<'overwrite' | 'copy'>('copy');
  const [bulkRunning, setBulkRunning] = React.useState<boolean>(false);
  const [bulkProgressOpen, setBulkProgressOpen] = React.useState<boolean>(false);
  const [bulkTotal, setBulkTotal] = React.useState<number>(0);
  const [bulkDone, setBulkDone] = React.useState<number>(0);
  const [bulkCurrent, setBulkCurrent] = React.useState<string>('');
  const [bulkFailed, setBulkFailed] = React.useState<number>(0);
  const [bulkSkipped, setBulkSkipped] = React.useState<number>(0);
  const [bulkFailures, setBulkFailures] = React.useState<Array<{ name: string; error: string; details?: string }>>([]);
  const [bulkFailureDialogOpen, setBulkFailureDialogOpen] = React.useState<boolean>(false);

  const [tableBulkDialogOpen, setTableBulkDialogOpen] = React.useState<boolean>(false);
  const [tableBulkMode, setTableBulkMode] = React.useState<'overwrite' | 'copy'>('copy');
  const [tableBulkRunning, setTableBulkRunning] = React.useState<boolean>(false);
  const [tableBulkProgressOpen, setTableBulkProgressOpen] = React.useState<boolean>(false);
  const [tableBulkTotal, setTableBulkTotal] = React.useState<number>(0);
  const [tableBulkDone, setTableBulkDone] = React.useState<number>(0);
  const [tableBulkFailed, setTableBulkFailed] = React.useState<number>(0);
  const [tableBulkCurrent, setTableBulkCurrent] = React.useState<string>('');
  const [tableBulkFailures, setTableBulkFailures] = React.useState<Array<{ name: string; error: string; details?: string }>>([]);
  const [tableBulkFailureDialogOpen, setTableBulkFailureDialogOpen] = React.useState<boolean>(false);

  const [tableRowBusy, setTableRowBusy] = React.useState<Record<string, boolean>>({});
  const [tableUploadOpen, setTableUploadOpen] = React.useState<boolean>(false);
  const [tableUploadTarget, setTableUploadTarget] = React.useState<{ schema: string; table: string } | null>(null);
  const [tableUploadLoading, setTableUploadLoading] = React.useState<boolean>(false);
  const [tableUploadApplying, setTableUploadApplying] = React.useState<boolean>(false);
  const [tableUploadBackups, setTableUploadBackups] = React.useState<Array<{ folderName: string; schemaPath: string; updatedAt?: string | null }>>([]);
  const [tableUploadSelected, setTableUploadSelected] = React.useState<string>('');

  const [applyAllOpen, setApplyAllOpen] = React.useState<boolean>(false);
  const [applyAllRunning, setApplyAllRunning] = React.useState<boolean>(false);
  const [applyAllProgressOpen, setApplyAllProgressOpen] = React.useState<boolean>(false);
  const [applyAllTotal, setApplyAllTotal] = React.useState<number>(0);
  const [applyAllDone, setApplyAllDone] = React.useState<number>(0);
  const [applyAllApplied, setApplyAllApplied] = React.useState<number>(0);
  const [applyAllSkipped, setApplyAllSkipped] = React.useState<number>(0);
  const [applyAllFailed, setApplyAllFailed] = React.useState<number>(0);
  const [applyAllCurrent, setApplyAllCurrent] = React.useState<string>('');
  const [applyAllResults, setApplyAllResults] = React.useState<
    Array<{ name: string; status: string; folderName?: string; message?: string; error?: string }>
  >([]);
  const [applyAllResultsOpen, setApplyAllResultsOpen] = React.useState<boolean>(false);

  const [uploadDialogOpen, setUploadDialogOpen] = React.useState<boolean>(false);
  const [uploadName, setUploadName] = React.useState<string>('');
  const [uploadCode, setUploadCode] = React.useState<string>('');
  const [uploadMode, setUploadMode] = React.useState<'editor' | 'file'>('editor');
  const [uploadBusy, setUploadBusy] = React.useState<boolean>(false);
  const uploadFileRef = React.useRef<HTMLInputElement | null>(null);

  const [bulkUploadDialogOpen, setBulkUploadDialogOpen] = React.useState<boolean>(false);
  const [bulkUploadRunning, setBulkUploadRunning] = React.useState<boolean>(false);
  const [bulkUploadProgressOpen, setBulkUploadProgressOpen] = React.useState<boolean>(false);
  const [bulkUploadTotal, setBulkUploadTotal] = React.useState<number>(0);
  const [bulkUploadDone, setBulkUploadDone] = React.useState<number>(0);
  const [bulkUploadFailed, setBulkUploadFailed] = React.useState<number>(0);
  const [bulkUploadCurrent, setBulkUploadCurrent] = React.useState<string>('');
  const [bulkUploadCandidates, setBulkUploadCandidates] = React.useState<Array<{ name: string; folder: string; fileName?: string }>>([]);
  const [bulkUploadFailures, setBulkUploadFailures] = React.useState<Array<{ name: string; error: string; details?: string }>>([]);
  const [bulkUploadFailureDialogOpen, setBulkUploadFailureDialogOpen] = React.useState<boolean>(false);
  const [expanded, setExpanded] = React.useState<ProviderId | false>('mistral');
  const [showSecrets, setShowSecrets] = React.useState<Record<string, boolean>>({});

  const [keysByProvider, setKeysByProvider] = React.useState<Record<ProviderId, ApiKeyRecord[]>>({
    mistral: [],
    groq: [],
    openai: [],
    anthropic: [],
  });

  const [editOpen, setEditOpen] = React.useState(false);
  const [editProvider, setEditProvider] = React.useState<ProviderId>('mistral');
  const [editId, setEditId] = React.useState<string | null>(null);
  const [editName, setEditName] = React.useState<string>('');
  const [editKey, setEditKey] = React.useState<string>('');

  const fetchAll = React.useCallback(async () => {
    setLoading(true);
    try {
      const token = await AdminKeyService.getSecret();
      if (!token) {
        throw new Error('Missing admin key');
      }

      const res = await fetch(API_ENDPOINT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Auth-Token': token,
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ action: 'list', token }),
      });
      const json = await res.json().catch(() => null);
      if (!res.ok || !json?.ok) {
        throw new Error(json?.error || `Failed to load integrations keys (${res.status})`);
      }
      const out: any = { ...keysByProvider };
      for (const p of PROVIDERS) {
        out[p.id] = Array.isArray(json?.providers?.[p.id]) ? json.providers[p.id] : [];
      }
      setKeysByProvider(out);
    } catch (e: any) {
      toast.error(e?.message || 'Failed to load API keys');
    } finally {
      setLoading(false);
    }
  }, [keysByProvider]);

  const saveSupabaseConnection = React.useCallback(async (nextUrl: string, nextAnonKey: string) => {
    setSavingSupabase(true);
    try {
      const token = await AdminKeyService.getSecret();
      if (!token) {
        throw new Error('Missing admin key');
      }
      const res = await fetch(API_ENDPOINT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Auth-Token': token,
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          action: 'supabase_set',
          url: nextUrl,
          anonKey: nextAnonKey,
          token,
        }),
      });
      const json = await res.json().catch(() => null);
      if (!res.ok || !json?.ok) {
        throw new Error(json?.error || `Failed to save Supabase connection (${res.status})`);
      }
      const c = json?.supabase;
      setSupabaseConn(c && typeof c === 'object' ? c : null);
      configureSupabase({ url: nextUrl, anonKey: nextAnonKey });
    } finally {
      setSavingSupabase(false);
    }
  }, []);

  const storageBackupRequest = React.useCallback(async (action: string, payload: any) => {
    const token = await AdminKeyService.getSecret();
    if (!token) throw new Error('Missing admin key');
    const res = await fetch(API_ENDPOINT, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Auth-Token': token,
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify({ action, token, ...(payload || {}) }),
    });
    const json = await res.json().catch(() => null);
    if (!res.ok || !json?.ok) {
      throw new Error(json?.error || `Request failed (${res.status})`);
    }
    return json;
  }, []);

  const backupAllBuckets = React.useCallback(async () => {
    if (bucketBackupAllRunning) return;
    setBucketBackupAllRunning(true);
    setStorageBackupMinimized(false);
    const toastId = toast.loading('Starting storage buckets backup…');
    try {
      const json = await storageBackupRequest('supabase_storage_backup_start', {});
      const job: StorageBackupJob = (json?.job && typeof json.job === 'object') ? json.job : null;
      if (!job?.jobId) throw new Error('Invalid job response');
      setStorageBackupJob(job);
      toast.success('Backup started', { id: toastId });
    } catch (e: any) {
      toast.error(e?.message || 'Failed to start backup', { id: toastId });
      setBucketBackupAllRunning(false);
    }
  }, [bucketBackupAllRunning, storageBackupRequest]);

  const pauseStorageBackup = React.useCallback(async () => {
    if (!storageBackupJob?.jobId) return;
    try {
      const json = await storageBackupRequest('supabase_storage_backup_pause', { jobId: storageBackupJob.jobId });
      const job: StorageBackupJob = (json?.job && typeof json.job === 'object') ? json.job : null;
      if (job) setStorageBackupJob(job);
    } catch (e: any) {
      toast.error(e?.message || 'Pause failed');
    }
  }, [storageBackupJob?.jobId, storageBackupRequest]);

  const resumeStorageBackup = React.useCallback(async () => {
    if (!storageBackupJob?.jobId) return;
    try {
      const json = await storageBackupRequest('supabase_storage_backup_resume', { jobId: storageBackupJob.jobId });
      const job: StorageBackupJob = (json?.job && typeof json.job === 'object') ? json.job : null;
      if (job) setStorageBackupJob(job);
    } catch (e: any) {
      toast.error(e?.message || 'Resume failed');
    }
  }, [storageBackupJob?.jobId, storageBackupRequest]);

  const cancelStorageBackup = React.useCallback(async () => {
    if (!storageBackupJob?.jobId) return;
    const toastId = toast.loading('Canceling backup…');
    try {
      const json = await storageBackupRequest('supabase_storage_backup_cancel', { jobId: storageBackupJob.jobId });
      const job: StorageBackupJob = (json?.job && typeof json.job === 'object') ? json.job : null;
      if (job) setStorageBackupJob(job);
      toast.success('Canceled', { id: toastId });
      closeStorageBackupPopup();
    } catch (e: any) {
      toast.error(e?.message || 'Cancel failed', { id: toastId });
    }
  }, [storageBackupJob?.jobId, storageBackupRequest, closeStorageBackupPopup]);

  React.useEffect(() => {
    const jobId = storageBackupJob?.jobId;
    const status = storageBackupJob?.status;
    if (!jobId) return;
    if (status === 'done' || status === 'canceled' || status === 'error') {
      setBucketBackupAllRunning(false);
      return;
    }

    const t = window.setInterval(async () => {
      if (storageBackupTickInFlight.current) return;
      if (!jobId) return;
      storageBackupTickInFlight.current = true;
      try {
        const json = await storageBackupRequest('supabase_storage_backup_tick', {
          jobId,
          maxFiles: 25,
          maxSeconds: 2.5,
        });
        const job: StorageBackupJob = (json?.job && typeof json.job === 'object') ? json.job : null;
        if (job) setStorageBackupJob(job);
      } catch (e: any) {
        toast.error(e?.message || 'Backup tick failed');
      } finally {
        storageBackupTickInFlight.current = false;
      }
    }, 1000);

    return () => {
      window.clearInterval(t);
    };
  }, [storageBackupJob?.jobId, storageBackupJob?.status, storageBackupRequest]);

  React.useEffect(() => {
    const job = storageBackupJob;
    if (!job?.jobId) return;
    if (job.status !== 'done') return;
    const found = Number(job.totalObjects || 0);
    const written = Number(job.written || 0);
    const failed = Number(job.failed || 0);
    const dir = String(job.backupDir || 'storage/backup/storage-buckets');
    const msg = `Saved to ${dir} · files ${written}/${found}${failed ? ` · ${failed} failed` : ''}`;
    if (failed) toast.error(msg, { duration: 8000 });
    else toast.success(msg, { duration: 6000 });
    window.setTimeout(() => closeStorageBackupPopup(), 1200);
  }, [storageBackupJob?.jobId, storageBackupJob?.status, closeStorageBackupPopup]);

  React.useEffect(() => {
    const job = storageBackupJob;
    if (!job?.jobId) return;
    if (job.status !== 'canceled') return;
    window.setTimeout(() => closeStorageBackupPopup(), 300);
  }, [storageBackupJob?.jobId, storageBackupJob?.status, closeStorageBackupPopup]);

  React.useEffect(() => {
    const job = storageBackupJob;
    if (!job?.jobId) return;
    if (job.status !== 'error') return;
    window.setTimeout(() => closeStorageBackupPopup(), 2500);
  }, [storageBackupJob?.jobId, storageBackupJob?.status, closeStorageBackupPopup]);

  React.useEffect(() => {
    if (!storageBackupJob?.jobId) return;
    if (storageBackupPos) return;
    const approxW = storageBackupMinimized ? 340 : 420;
    const x = Math.max(16, (window.innerWidth || 0) - approxW - 16);
    const y = 16;
    setStorageBackupPos({ x, y });
  }, [storageBackupJob?.jobId, storageBackupMinimized, storageBackupPos]);

  React.useEffect(() => {
    const onPointerMove = (e: PointerEvent) => {
      if (!storageBackupDragActive.current) return;
      setStorageBackupPos((prev) => {
        const p = prev || { x: 16, y: 16 };
        const rect = storageBackupPopupRef.current?.getBoundingClientRect();
        const w = rect?.width || (storageBackupMinimized ? 340 : 420);
        const h = rect?.height || 180;
        const nx = e.clientX - storageBackupDragOffset.current.dx;
        const ny = e.clientY - storageBackupDragOffset.current.dy;
        const maxX = Math.max(0, (window.innerWidth || 0) - w);
        const maxY = Math.max(0, (window.innerHeight || 0) - h);
        return {
          x: Math.max(0, Math.min(nx, maxX)),
          y: Math.max(0, Math.min(ny, maxY)),
        };
      });
    };

    const onPointerUp = () => {
      if (!storageBackupDragActive.current) return;
      storageBackupDragActive.current = false;
    };

    window.addEventListener('pointermove', onPointerMove);
    window.addEventListener('pointerup', onPointerUp);
    return () => {
      window.removeEventListener('pointermove', onPointerMove);
      window.removeEventListener('pointerup', onPointerUp);
    };
  }, [storageBackupMinimized]);

  const downloadBucketSql = React.useCallback(async (bucketId: string, bucketName: string) => {
    const key = String(bucketId || bucketName || '').trim();
    if (!key) return;
    setBucketSqlBusy((p) => ({ ...p, [key]: true }));
    const toastId = toast.loading('Preparing bucket SQL...');
    try {
      const token = await AdminKeyService.getSecret();
      if (!token) throw new Error('Missing admin key');
      const res = await fetch(API_ENDPOINT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Auth-Token': token,
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          action: 'supabase_bucket_download_sql',
          token,
          bucketId,
          bucketName,
        }),
      });
      if (!res.ok) {
        const j = await res.json().catch(() => null);
        throw new Error(j?.error || `Download failed (${res.status})`);
      }

      const blob = await res.blob();
      const cd = res.headers.get('content-disposition') || '';
      const m = cd.match(/filename\s*=\s*"?([^";]+)"?/i);
      const derived = m?.[1] ? String(m[1]).trim() : '';
      const safe = (bucketName || bucketId || 'bucket').replace(/[^a-z0-9._-]+/gi, '-');
      const fileName = derived || `${safe || 'bucket'}.sql`;

      const url = URL.createObjectURL(blob);
      try {
        const a = document.createElement('a');
        a.href = url;
        a.download = fileName;
        document.body.appendChild(a);
        a.click();
        a.remove();
      } finally {
        URL.revokeObjectURL(url);
      }

      toast.success('Download started', { id: toastId });
    } catch (e: any) {
      toast.error(e?.message || 'Failed to download bucket SQL', { id: toastId });
    } finally {
      setBucketSqlBusy((p) => ({ ...p, [key]: false }));
    }
  }, []);

  const downloadBucketZip = React.useCallback(async (bucketId: string, bucketName: string) => {
    const key = String(bucketId || bucketName || '').trim();
    if (!key) return;
    setBucketZipBusy((p) => ({ ...p, [key]: true }));
    const toastId = toast.loading('Preparing bucket zip...');
    try {
      const token = await AdminKeyService.getSecret();
      if (!token) throw new Error('Missing admin key');
      const res = await fetch(API_ENDPOINT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Auth-Token': token,
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          action: 'supabase_bucket_download_zip',
          token,
          bucketId,
          bucketName,
        }),
      });

      if (!res.ok) {
        const j = await res.json().catch(() => null);
        throw new Error(j?.error || `Download failed (${res.status})`);
      }

      const blob = await res.blob();
      const cd = res.headers.get('content-disposition') || '';
      const m = cd.match(/filename\s*=\s*"?([^";]+)"?/i);
      const derived = m?.[1] ? String(m[1]).trim() : '';
      const safe = (bucketName || bucketId || 'bucket').replace(/[^a-z0-9._-]+/gi, '-');
      const fileName = derived || `${safe || 'bucket'}.zip`;
      const url = URL.createObjectURL(blob);
      try {
        const a = document.createElement('a');
        a.href = url;
        a.download = fileName;
        document.body.appendChild(a);
        a.click();
        a.remove();
      } finally {
        URL.revokeObjectURL(url);
      }

      toast.success('Download started', { id: toastId });
    } catch (e: any) {
      toast.error(e?.message || 'Failed to download bucket zip', { id: toastId });
    } finally {
      setBucketZipBusy((p) => ({ ...p, [key]: false }));
    }
  }, []);

  const fetchTableBackupsLocal = React.useCallback(async (force?: boolean) => {
    if (tableBackupsInFlight.current) return;
    if (!force && tableBackupsLoaded) return;
    tableBackupsInFlight.current = true;
    setLoadingTableBackups(true);
    try {
      const token = await AdminKeyService.getSecret();
      if (!token) {
        throw new Error('Missing admin key');
      }
      const res = await fetch(API_ENDPOINT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Auth-Token': token,
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ action: 'supabase_table_schema_backups_overview_local', token }),
      });
      const json = await res.json().catch(() => null);
      if (!res.ok || !json?.ok) {
        throw new Error(json?.error || `Failed to list table backups (${res.status})`);
      }
      const arr = Array.isArray(json?.backups) ? json.backups : [];
      setTableLocalBackups(
        arr
          .map((x: any) => ({
            schema: String(x?.schema || ''),
            table: String(x?.table || ''),
            folderName: String(x?.folderName || ''),
            schemaPath: String(x?.schemaPath || ''),
            updatedAt: x?.updatedAt ? String(x.updatedAt) : null,
          }))
          .filter((x: any) => x.schema && x.table && x.folderName)
      );
      setTableBackupsLoaded(true);
    } catch (e: any) {
      toast.error(e?.message || 'Failed to list table backups');
    } finally {
      setLoadingTableBackups(false);
      tableBackupsInFlight.current = false;
    }
  }, [tableBackupsLoaded]);

  const fetchEdgeBackupsLocal = React.useCallback(async (force?: boolean) => {
    if (edgeBackupsInFlight.current) return;
    if (!force && edgeBackupsLoaded) return;
    edgeBackupsInFlight.current = true;
    setLoadingEdgeBackups(true);
    try {
      const token = await AdminKeyService.getSecret();
      if (!token) {
        throw new Error('Missing admin key');
      }
      const res = await fetch(API_ENDPOINT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Auth-Token': token,
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ action: 'supabase_edge_backups_list_local', token }),
      });
      const json = await res.json().catch(() => null);
      if (!res.ok || !json?.ok) {
        throw new Error(json?.error || `Failed to list edge backups (${res.status})`);
      }
      const arr = Array.isArray(json?.backups) ? json.backups : [];
      setEdgeLocalBackups(
        arr
          .map((x: any) => ({
            name: String(x?.name || ''),
            folder: String(x?.folder || ''),
            status: String(x?.status || ''),
            fileName: x?.fileName ? String(x.fileName) : undefined,
            updatedAt: x?.updatedAt ? String(x.updatedAt) : null,
          }))
          .filter((x: any) => x.name && x.folder)
      );
      setEdgeBackupsLoaded(true);
    } catch (e: any) {
      toast.error(e?.message || 'Failed to list Edge Function backups');
    } finally {
      setLoadingEdgeBackups(false);
      edgeBackupsInFlight.current = false;
    }
  }, [edgeBackupsLoaded]);

  const fetchOverview = React.useCallback(async () => {
    setLoadingOverview(true);
    try {
      const token = await AdminKeyService.getSecret();
      if (!token) {
        throw new Error('Missing admin key');
      }
      const res = await fetch(API_ENDPOINT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Auth-Token': token,
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ action: 'supabase_overview', token }),
      });
      const json = await res.json().catch(() => null);
      if (!res.ok || !json?.ok) {
        throw new Error(json?.error || `Failed to load overview (${res.status})`);
      }
      const ov = json?.overview && typeof json.overview === 'object' ? json.overview : null;
      setOverview(ov);
    } catch (e: any) {
      toast.error(e?.message || 'Failed to load overview');
    } finally {
      setLoadingOverview(false);
    }
  }, []);

  const openUploadDialog = React.useCallback(() => {
    setUploadName('');
    setUploadCode('');
    setUploadMode('editor');
    setUploadDialogOpen(true);
  }, []);

  const openUploadDialogForFunction = React.useCallback((fnName: string) => {
    setUploadName(fnName);
    setUploadCode('');
    setUploadMode('editor');
    setUploadDialogOpen(true);
  }, []);

  const onPickUploadFile = React.useCallback(() => {
    if (uploadFileRef.current) {
      uploadFileRef.current.click();
    }
  }, []);

  const onUploadFileChange = React.useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files && e.target.files.length > 0 ? e.target.files[0] : null;
    e.target.value = '';
    if (!f) return;
    if (!f.name.toLowerCase().endsWith('.ts')) {
      toast.error('Please select a .ts file');
      return;
    }
    const txt = await f.text().catch(() => '');
    if (!txt.trim()) {
      toast.error('File is empty');
      return;
    }
    setUploadMode('file');
    setUploadCode(txt);
    if (!uploadName.trim()) {
      const base = f.name.replace(/\.ts$/i, '');
      setUploadName(base);
    }
  }, [uploadName]);

  const deployEdgeFunction = React.useCallback(async () => {
    if (uploadBusy) return;
    const fn = uploadName.trim();
    const code = uploadCode;
    if (!fn) {
      toast.error('Please enter a function name');
      return;
    }
    if (!/^[A-Za-z][A-Za-z0-9_-]*$/.test(fn)) {
      toast.error('Invalid function name');
      return;
    }
    if (!code.trim()) {
      toast.error('Please provide function code');
      return;
    }
    setUploadBusy(true);
    const id = toast.loading(`Deploying ${fn}...`);
    try {
      const token = await AdminKeyService.getSecret();
      if (!token) {
        throw new Error('Missing admin key');
      }
      const res = await fetch(API_ENDPOINT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Auth-Token': token,
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ action: 'supabase_edge_deploy', name: fn, code, verifyJwt: false, token }),
      });
      const json = await res.json().catch(() => null);
      if (!res.ok || !json?.ok) {
        const base = String(json?.error || `Deploy failed (${res.status})`);
        const details = json?.details ? String(json.details) : '';
        const hint = json?.hint ? String(json.hint) : '';
        const extra = [hint || null, details || null].filter(Boolean).join(' | ');
        throw new Error(extra ? `${base} (${extra})` : base);
      }
      toast.success('Deployed', { id });
      setUploadDialogOpen(false);
      await fetchOverview();
    } catch (e: any) {
      toast.error(e?.message || 'Deploy failed', { id });
    } finally {
      setUploadBusy(false);
    }
  }, [fetchOverview, uploadBusy, uploadCode, uploadName]);

  const deployEdgeFunctionFromBackup = React.useCallback(async (fnName: string) => {
    const name = String(fnName || '').trim();
    if (!name) return;
    if (deployingBackupFn[name]) return;
    setDeployingBackupFn((p) => ({ ...p, [name]: true }));
    const id = toast.loading(`Deploying ${name} from backup...`);
    try {
      const token = await AdminKeyService.getSecret();
      if (!token) {
        throw new Error('Missing admin key');
      }
      const res = await fetch(API_ENDPOINT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Auth-Token': token,
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ action: 'supabase_edge_deploy_backup', name, verifyJwt: false, token }),
      });
      const json = await res.json().catch(() => null);
      if (!res.ok || !json?.ok) {
        const base = String(json?.error || `Deploy failed (${res.status})`);
        const details = json?.details ? String(json.details) : '';
        const hint = json?.hint ? String(json.hint) : '';
        const extra = [hint || null, details || null].filter(Boolean).join(' | ');
        throw new Error(extra ? `${base} (${extra})` : base);
      }
      toast.success('Deployed', { id });
      if (edgeSource === 'supabase') {
        await fetchOverview();
      }
    } catch (e: any) {
      toast.error(e?.message || 'Deploy failed', { id });
    } finally {
      setDeployingBackupFn((p) => {
        const next = { ...p };
        delete next[name];
        return next;
      });
    }
  }, [deployingBackupFn, edgeSource, fetchOverview]);

  const applyLatestTableBackup = React.useCallback(async (schema: string, table: string, folderName: string) => {
    const s = String(schema || '').trim();
    const t = String(table || '').trim();
    const f = String(folderName || '').trim();
    if (!s || !t || !f) return;
    const key = `${s}.${t}`;
    setTableRowBusy((p) => ({ ...p, [key]: true }));
    const id = toast.loading(`Applying ${key}...`);
    try {
      const token = await AdminKeyService.getSecret();
      if (!token) {
        throw new Error('Missing admin key');
      }
      const res = await fetch(API_ENDPOINT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Auth-Token': token,
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ action: 'supabase_table_schema_apply', schema: s, table: t, folderName: f, token }),
      });
      const json = await res.json().catch(() => null);
      if (!res.ok || !json?.ok) {
        throw new Error(json?.error || `Apply failed (${res.status})`);
      }
      if (json?.status === 'skipped_exists') {
        toast.success('Skipped (already exists)', { id });
      } else {
        toast.success('Applied', { id });
      }
    } catch (e: any) {
      toast.error(e?.message || 'Apply failed', { id });
    } finally {
      setTableRowBusy((p) => {
        const next = { ...p };
        delete next[key];
        return next;
      });
    }
  }, []);

  const openBulkUploadDialog = React.useCallback(async () => {
    if (bulkUploadRunning) return;
    const id = toast.loading('Scanning backups...');
    try {
      const token = await AdminKeyService.getSecret();
      if (!token) {
        throw new Error('Missing admin key');
      }
      const res = await fetch(API_ENDPOINT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Auth-Token': token,
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ action: 'supabase_edge_backups_overview', token }),
      });
      const json = await res.json().catch(() => null);
      if (!res.ok || !json?.ok) {
        const base = String(json?.error || `Failed to scan backups (${res.status})`);
        const details = json?.details ? String(json.details) : '';
        const hint = json?.hint ? String(json.hint) : '';
        const extra = [hint || null, details || null].filter(Boolean).join(' | ');
        throw new Error(extra ? `${base} (${extra})` : base);
      }
      const missing = Array.isArray(json?.missing) ? json.missing : [];
      const items = missing
        .map((m: any) => ({
          name: String(m?.name || '').trim(),
          folder: String(m?.folder || '').trim(),
          fileName: m?.fileName ? String(m.fileName) : undefined,
        }))
        .filter((x: any) => x.name && x.folder);
      setBulkUploadCandidates(items);
      setBulkUploadDialogOpen(true);
      toast.success(items.length > 0 ? `Found ${items.length} missing function(s)` : 'No missing functions found', { id });
    } catch (e: any) {
      toast.error(e?.message || 'Failed to scan backups', { id });
    }
  }, [bulkUploadRunning]);

  const startBulkUpload = React.useCallback(async () => {
    if (bulkUploadRunning) return;
    if (!bulkUploadCandidates || bulkUploadCandidates.length === 0) {
      toast.error('No missing functions found');
      return;
    }
    setBulkUploadDialogOpen(false);
    setBulkUploadProgressOpen(true);
    setBulkUploadRunning(true);
    setBulkUploadTotal(bulkUploadCandidates.length);
    setBulkUploadDone(0);
    setBulkUploadFailed(0);
    setBulkUploadCurrent('');
    setBulkUploadFailures([]);

    const id = toast.loading('Uploading missing functions...');
    try {
      const token = await AdminKeyService.getSecret();
      if (!token) {
        throw new Error('Missing admin key');
      }
      let failed = 0;
      const failedDetails: Array<{ name: string; error: string; details?: string }> = [];
      for (let i = 0; i < bulkUploadCandidates.length; i += 1) {
        const fn = bulkUploadCandidates[i];
        setBulkUploadCurrent(fn.name);
        const res = await fetch(API_ENDPOINT, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Auth-Token': token,
            'Authorization': `Bearer ${token}`,
          },
          body: JSON.stringify({ action: 'supabase_edge_deploy_backup', name: fn.name, verifyJwt: false, token }),
        });
        const json = await res.json().catch(() => null);
        if (!res.ok || !json?.ok) {
          failed += 1;
          failedDetails.push({
            name: fn.name,
            error: String(json?.error || `Deploy failed (${res.status})`),
            details: json?.details ? String(json.details) : (json?.hint ? String(json.hint) : undefined),
          });
          setBulkUploadFailed(failed);
        }
        setBulkUploadDone(i + 1);
      }
      if (failed > 0) {
        setBulkUploadFailures(failedDetails);
        setBulkUploadFailureDialogOpen(true);
        toast.error(`Upload complete with ${failed} failure(s)`, { id });
      } else {
        toast.success('Upload complete', { id });
      }
      await fetchOverview();
    } catch (e: any) {
      toast.error(e?.message || 'Upload failed', { id });
    } finally {
      setBulkUploadRunning(false);
      setBulkUploadCurrent('');
      setBulkUploadProgressOpen(false);
    }
  }, [bulkUploadCandidates, bulkUploadRunning, fetchOverview]);

  const openDeleteDialog = React.useCallback((name: string) => {
    const fn = String(name || '').trim();
    setDeleteTarget(fn);
    setDeleteConfirm('');
    setDeleteBackupFirst(true);
    setDeleteDialogOpen(true);
  }, []);

  const backupEdgeFunction = React.useCallback(async (name: string, mode?: 'overwrite' | 'copy') => {
    const fn = String(name || '').trim();
    if (!fn) return;
    setDownloadingFn((p) => ({ ...p, [fn]: true }));
    const id = toast.loading(`Backing up ${fn}...`);
    try {
      const token = await AdminKeyService.getSecret();
      if (!token) {
        throw new Error('Missing admin key');
      }
      const res = await fetch(API_ENDPOINT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Auth-Token': token,
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ action: 'supabase_edge_backup', name: fn, mode: mode || 'overwrite', token }),
      });
      const json = await res.json().catch(() => null);
      if (!res.ok || !json?.ok) {
        if (json?.code === 'missing_local_source') {
          throw new Error(json?.hint || json?.error || 'Missing local function source');
        }
        throw new Error(json?.error || `Backup failed (${res.status})`);
      }
      const savedPath = json?.savedPath ? String(json.savedPath) : '';
      toast.success(savedPath ? `Saved to ${savedPath}` : 'Backed up', { id });
    } catch (e: any) {
      toast.error(e?.message || 'Backup failed', { id });
    } finally {
      setDownloadingFn((p) => ({ ...p, [fn]: false }));
    }
  }, []);

  const deleteEdgeFunction = React.useCallback(async (name: string, backupFirst: boolean) => {
    const fn = String(name || '').trim();
    if (!fn) return;
    setDeletingFn((p) => ({ ...p, [fn]: true }));
    const id = toast.loading(`Deleting ${fn}...`);
    try {
      const token = await AdminKeyService.getSecret();
      if (!token) {
        throw new Error('Missing admin key');
      }
      if (backupFirst) {
        await backupEdgeFunction(fn, 'copy');
      }
      const res = await fetch(API_ENDPOINT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Auth-Token': token,
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ action: 'supabase_edge_delete', name: fn, token }),
      });
      const json = await res.json().catch(() => null);
      if (!res.ok || !json?.ok) {
        const base = String(json?.error || `Delete failed (${res.status})`);
        const details = json?.details ? String(json.details) : '';
        const extra = [
          details ? details : null,
          json?.httpStatus != null ? `httpStatus=${String(json.httpStatus)}` : null,
          json?.curlErrno != null ? `curlErrno=${String(json.curlErrno)}` : null,
        ].filter(Boolean).join(' | ');
        throw new Error(extra ? `${base} (${extra})` : base);
      }
      toast.success('Deleted', { id });
      setDeleteDialogOpen(false);
      await fetchOverview();
    } catch (e: any) {
      toast.error(e?.message || 'Delete failed', { id });
    } finally {
      setDeletingFn((p) => ({ ...p, [fn]: false }));
    }
  }, [backupEdgeFunction, fetchOverview]);

  const openDeleteBackupDialog = React.useCallback(async (name: string) => {
    const fn = String(name || '').trim();
    if (!fn) return;
    setDeleteBackupTarget(fn);
    setDeleteBackupDialogOpen(true);
    setDeleteBackupLoading(true);
    setDeleteBackupVariants([]);
    setDeleteBackupSelected({});
    try {
      const token = await AdminKeyService.getSecret();
      if (!token) {
        throw new Error('Missing admin key');
      }
      const res = await fetch(API_ENDPOINT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Auth-Token': token,
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ action: 'supabase_edge_backup_variants_local', name: fn, token }),
      });
      const json = await res.json().catch(() => null);
      if (!res.ok || !json?.ok) {
        throw new Error(json?.error || `Failed to list backup folders (${res.status})`);
      }
      const variants: Array<{ folder: string; n: number; updatedAt?: string | null }> = Array.isArray(json?.variants)
        ? json.variants
          .map((v: any) => ({
            folder: String(v?.folder || ''),
            n: Number(v?.n || 0),
            updatedAt: v?.updatedAt ? String(v.updatedAt) : null,
          }))
          .filter((v: any) => !!v.folder)
        : [];
      setDeleteBackupVariants(variants);
      const defaults: Record<string, boolean> = {};
      variants.forEach((v) => {
        defaults[v.folder] = true;
      });
      setDeleteBackupSelected(defaults);
    } catch (e: any) {
      toast.error(e?.message || 'Failed to load backup folders');
      setDeleteBackupDialogOpen(false);
    } finally {
      setDeleteBackupLoading(false);
    }
  }, []);

  const runDeleteBackupFolders = React.useCallback(async () => {
    const name = String(deleteBackupTarget || '').trim();
    if (!name) return;
    if (deleteBackupRunning) return;
    const folders = Object.keys(deleteBackupSelected || {}).filter((k) => !!deleteBackupSelected[k]);
    if (folders.length === 0) {
      toast.error('Select at least one folder to delete');
      return;
    }

    setDeleteBackupRunning(true);
    const id = toast.loading(`Deleting ${folders.length} backup folder(s)...`);
    try {
      const token = await AdminKeyService.getSecret();
      if (!token) {
        throw new Error('Missing admin key');
      }
      const res = await fetch(API_ENDPOINT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Auth-Token': token,
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ action: 'supabase_edge_backup_delete_local', name, folders, token }),
      });
      const json = await res.json().catch(() => null);
      if (!res.ok || !json?.ok) {
        throw new Error(json?.error || `Delete failed (${res.status})`);
      }
      const deleted = Array.isArray(json?.deleted) ? json.deleted.length : 0;
      const skipped = Array.isArray(json?.skipped) ? json.skipped.length : 0;
      const missing = Array.isArray(json?.missing) ? json.missing.length : 0;
      const parts = [
        deleted ? `${deleted} deleted` : null,
        skipped ? `${skipped} skipped` : null,
        missing ? `${missing} missing` : null,
      ].filter(Boolean).join(' · ');
      toast.success(parts || 'Deleted', { id });
      setDeleteBackupDialogOpen(false);
      await fetchEdgeBackupsLocal(true);
    } catch (e: any) {
      toast.error(e?.message || 'Delete failed', { id });
    } finally {
      setDeleteBackupRunning(false);
    }
  }, [deleteBackupRunning, deleteBackupSelected, deleteBackupTarget, fetchEdgeBackupsLocal]);

  const openBulkDeleteBackupsDialog = React.useCallback(async () => {
    setBulkDeleteBackupsOpen(true);
    setBulkDeleteBackupsLoading(true);
    setBulkDeleteBackupsData([]);
    setBulkDeleteBackupsSelected({});
    try {
      const token = await AdminKeyService.getSecret();
      if (!token) {
        throw new Error('Missing admin key');
      }
      const res = await fetch(API_ENDPOINT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Auth-Token': token,
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ action: 'supabase_edge_backup_variants_overview_local', token }),
      });
      const json = await res.json().catch(() => null);
      if (!res.ok || !json?.ok) {
        throw new Error(json?.error || `Failed to list backup folders (${res.status})`);
      }
      const functions: Array<{ name: string; variants: Array<{ folder: string; n: number; updatedAt?: string | null }> }> = Array.isArray(json?.functions)
        ? json.functions
          .map((x: any) => ({
            name: String(x?.name || ''),
            variants: Array.isArray(x?.variants)
              ? x.variants
                .map((v: any) => ({
                  folder: String(v?.folder || ''),
                  n: Number(v?.n || 0),
                  updatedAt: v?.updatedAt ? String(v.updatedAt) : null,
                }))
                .filter((v: any) => !!v.folder)
              : [],
          }))
          .filter((x: any) => !!x.name)
        : [];
      setBulkDeleteBackupsData(functions);
      const defaults: Record<string, boolean> = {};
      functions.forEach((fn) => {
        fn.variants.forEach((v) => {
          defaults[v.folder] = true;
        });
      });
      setBulkDeleteBackupsSelected(defaults);
    } catch (e: any) {
      toast.error(e?.message || 'Failed to load backup folders');
      setBulkDeleteBackupsOpen(false);
    } finally {
      setBulkDeleteBackupsLoading(false);
    }
  }, []);

  const runBulkDeleteBackups = React.useCallback(async () => {
    if (bulkDeleteBackupsRunning) return;
    const folders = Object.keys(bulkDeleteBackupsSelected || {}).filter((k) => !!bulkDeleteBackupsSelected[k]);
    if (folders.length === 0) {
      toast.error('Select at least one folder to delete');
      return;
    }
    setBulkDeleteBackupsRunning(true);
    const id = toast.loading(`Deleting ${folders.length} backup folder(s)...`);
    try {
      const token = await AdminKeyService.getSecret();
      if (!token) {
        throw new Error('Missing admin key');
      }
      const res = await fetch(API_ENDPOINT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Auth-Token': token,
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ action: 'supabase_edge_backup_delete_many_local', folders, token }),
      });
      const json = await res.json().catch(() => null);
      if (!res.ok || !json?.ok) {
        throw new Error(json?.error || `Delete failed (${res.status})`);
      }
      const deleted = Array.isArray(json?.deleted) ? json.deleted.length : 0;
      const skipped = Array.isArray(json?.skipped) ? json.skipped.length : 0;
      const missing = Array.isArray(json?.missing) ? json.missing.length : 0;
      const parts = [
        deleted ? `${deleted} deleted` : null,
        skipped ? `${skipped} skipped` : null,
        missing ? `${missing} missing` : null,
      ].filter(Boolean).join(' · ');
      toast.success(parts || 'Deleted', { id });
      setBulkDeleteBackupsOpen(false);
      await fetchEdgeBackupsLocal(true);
    } catch (e: any) {
      toast.error(e?.message || 'Delete failed', { id });
    } finally {
      setBulkDeleteBackupsRunning(false);
    }
  }, [bulkDeleteBackupsRunning, bulkDeleteBackupsSelected, fetchEdgeBackupsLocal]);

  const startBulkBackup = React.useCallback(async () => {
    if (bulkRunning) return;
    const arr = Array.isArray(overview?.functions) ? overview!.functions! : [];
    const names = arr
      .map((f: any) => String(f?.name || f?.slug || '').trim())
      .filter((n: string) => n);
    const unique = Array.from(new Set(names));
    if (unique.length === 0) {
      toast.error('No edge functions detected');
      return;
    }

    setBulkDialogOpen(false);
    setBulkProgressOpen(true);
    setBulkRunning(true);
    setBulkTotal(unique.length);
    setBulkDone(0);
    setBulkFailed(0);
    setBulkSkipped(0);
    setBulkFailures([]);
    setBulkCurrent('');

    try {
      const token = await AdminKeyService.getSecret();
      if (!token) {
        throw new Error('Missing admin key');
      }
      let failed = 0;
      const failedNames: string[] = [];
      const failedDetails: Array<{ name: string; error: string; details?: string }> = [];
      let skipped = 0;
      for (let i = 0; i < unique.length; i += 1) {
        const fn = unique[i];
        setBulkCurrent(fn);
        const res = await fetch(API_ENDPOINT, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Auth-Token': token,
            'Authorization': `Bearer ${token}`,
          },
          body: JSON.stringify({ action: 'supabase_edge_backup', name: fn, mode: bulkMode, token }),
        });
        const json = await res.json().catch(() => null);
        if (!res.ok || !json?.ok) {
          if (json?.code === 'missing_local_source') {
            skipped += 1;
            setBulkSkipped(skipped);
            setBulkDone(i + 1);
            continue;
          }
          failed += 1;
          failedNames.push(fn);
          failedDetails.push({
            name: fn,
            error: String(json?.error || `Backup failed (${res.status})`),
            details: json?.details ? String(json.details) : undefined,
          });
          setBulkFailed(failed);
          setBulkDone(i + 1);
          continue;
        }
        setBulkDone(i + 1);
      }
      if (failed > 0) {
        setBulkFailures(failedDetails);
        setBulkFailureDialogOpen(true);
        const firstFew = failedNames.slice(0, 3).join(', ');
        const suffix = failedNames.length > 3 ? '…' : '';
        toast.error(`Backup completed with ${failed} failure(s). Failed: ${firstFew}${suffix}`);
      } else if (skipped > 0) {
        toast.success(`Backup completed (${skipped} skipped: not in repo)`);
      } else {
        toast.success('Edge Functions backup completed');
      }
      setBulkProgressOpen(false);
    } catch (e: any) {
      toast.error(e?.message || 'Bulk backup failed');
    } finally {
      setBulkRunning(false);
      setBulkCurrent('');
    }
  }, [bulkMode, bulkRunning, overview]);

  const startTableBulkBackup = React.useCallback(async () => {
    if (tableBulkRunning) return;
    const rows = Array.isArray(overview?.tables) ? overview!.tables! : [];
    const list = rows
      .filter((t: any) => String(t?.kind || '') === 'table' || String(t?.kind || '') === 'partitioned_table')
      .map((t: any) => ({
        schema: String(t?.schema || '').trim(),
        name: String(t?.name || '').trim(),
      }))
      .filter((t: any) => t.schema && t.name);

    if (list.length === 0) {
      toast.error('No tables detected');
      return;
    }

    setTableBulkDialogOpen(false);
    setTableBulkProgressOpen(true);
    setTableBulkRunning(true);
    setTableBulkTotal(list.length);
    setTableBulkDone(0);
    setTableBulkFailed(0);
    setTableBulkCurrent('');
    setTableBulkFailures([]);

    const id = toast.loading('Downloading tables...');
    try {
      const token = await AdminKeyService.getSecret();
      if (!token) {
        throw new Error('Missing admin key');
      }
      let failed = 0;
      const failedDetails: Array<{ name: string; error: string; details?: string }> = [];
      for (let i = 0; i < list.length; i += 1) {
        const t = list[i];
        const label = `${t.schema}.${t.name}`;
        setTableBulkCurrent(label);
        const res = await fetch(API_ENDPOINT, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Auth-Token': token,
            'Authorization': `Bearer ${token}`,
          },
          body: JSON.stringify({
            action: 'supabase_table_schema_backup',
            schema: t.schema,
            table: t.name,
            mode: tableBulkMode,
            token,
          }),
        });
        const json = await res.json().catch(() => null);
        if (!res.ok || !json?.ok) {
          failed += 1;
          failedDetails.push({
            name: label,
            error: String(json?.error || `Backup failed (${res.status})`),
            details: json?.details ? String(json.details) : undefined,
          });
          setTableBulkFailed(failed);
        }
        setTableBulkDone(i + 1);
      }
      if (failed > 0) {
        setTableBulkFailures(failedDetails);
        setTableBulkFailureDialogOpen(true);
        toast.error(`Tables backup completed with ${failed} failure(s)`, { id });
      } else {
        toast.success('Tables backup completed', { id });
      }
    } catch (e: any) {
      toast.error(e?.message || 'Tables backup failed', { id });
    } finally {
      setTableBulkRunning(false);
      setTableBulkCurrent('');
      setTableBulkProgressOpen(false);
    }
  }, [overview, tableBulkMode, tableBulkRunning]);

  const downloadOneTableSchema = React.useCallback(
    async (schema: string, table: string) => {
      const key = `${schema}.${table}`;
      if (tableRowBusy[key]) return;
      setTableRowBusy((p) => ({ ...p, [key]: true }));
      const id = toast.loading(`Downloading ${key}...`);
      try {
        const token = await AdminKeyService.getSecret();
        if (!token) throw new Error('Missing admin key');
        const res = await fetch(API_ENDPOINT, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Auth-Token': token,
            'Authorization': `Bearer ${token}`,
          },
          body: JSON.stringify({
            action: 'supabase_table_schema_backup',
            schema,
            table,
            mode: 'overwrite',
            token,
          }),
        });
        const json = await res.json().catch(() => null);
        if (!res.ok || !json?.ok) {
          throw new Error(String(json?.error || `Failed (${res.status})`));
        }
        toast.success(`Saved ${key} schema.sql`, { id });
      } catch (e: any) {
        toast.error(e?.message || 'Download failed', { id });
      } finally {
        setTableRowBusy((p) => ({ ...p, [key]: false }));
      }
    },
    [tableRowBusy]
  );

  const openTableUpload = React.useCallback(async (schema: string, table: string) => {
    setTableUploadOpen(true);
    setTableUploadTarget({ schema, table });
    setTableUploadSelected('');
    setTableUploadBackups([]);
    setTableUploadLoading(true);
    try {
      const token = await AdminKeyService.getSecret();
      if (!token) throw new Error('Missing admin key');
      const res = await fetch(API_ENDPOINT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Auth-Token': token,
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          action: 'supabase_table_schema_backups_list',
          schema,
          table,
          token,
        }),
      });
      const json = await res.json().catch(() => null);
      if (!res.ok || !json?.ok) {
        throw new Error(String(json?.error || `Failed to load backups (${res.status})`));
      }
      const list = Array.isArray(json?.backups) ? (json.backups as any[]) : [];
      const cleaned = list
        .map((b) => ({
          folderName: String(b?.folderName || ''),
          schemaPath: String(b?.schemaPath || ''),
          updatedAt: b?.updatedAt ? String(b.updatedAt) : null,
        }))
        .filter((b) => b.folderName && b.schemaPath);
      setTableUploadBackups(cleaned);
      if (cleaned.length > 0) {
        setTableUploadSelected(cleaned[0].folderName);
      }
    } catch (e: any) {
      toast.error(e?.message || 'Failed to load backups');
    } finally {
      setTableUploadLoading(false);
    }
  }, []);

  const applyTableSchemaBackup = React.useCallback(async () => {
    if (!tableUploadTarget || !tableUploadSelected || tableUploadApplying) return;
    setTableUploadApplying(true);
    const key = `${tableUploadTarget.schema}.${tableUploadTarget.table}`;
    const id = toast.loading(`Applying schema for ${key}...`);
    try {
      const token = await AdminKeyService.getSecret();
      if (!token) throw new Error('Missing admin key');
      const res = await fetch(API_ENDPOINT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Auth-Token': token,
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          action: 'supabase_table_schema_apply',
          schema: tableUploadTarget.schema,
          table: tableUploadTarget.table,
          folderName: tableUploadSelected,
          token,
        }),
      });
      const json = await res.json().catch(() => null);
      if (!res.ok || !json?.ok) {
        throw new Error(String(json?.error || `Apply failed (${res.status})`));
      }
      toast.success('Schema applied', { id });
      setTableUploadOpen(false);
      await fetchOverview();
    } catch (e: any) {
      toast.error(e?.message || 'Apply failed', { id });
    } finally {
      setTableUploadApplying(false);
    }
  }, [fetchOverview, tableUploadApplying, tableUploadSelected, tableUploadTarget]);

  const applyAllSchemas = React.useCallback(async () => {
    if (applyAllRunning) return;
    setApplyAllOpen(false);
    setApplyAllProgressOpen(true);
    setApplyAllRunning(true);
    setApplyAllTotal(0);
    setApplyAllDone(0);
    setApplyAllApplied(0);
    setApplyAllSkipped(0);
    setApplyAllFailed(0);
    setApplyAllCurrent('');
    setApplyAllResults([]);

    const id = toast.loading('Applying all table schemas...');
    try {
      const token = await AdminKeyService.getSecret();
      if (!token) throw new Error('Missing admin key');
      const res = await fetch(API_ENDPOINT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Auth-Token': token,
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          action: 'supabase_table_schema_apply_all',
          token,
        }),
      });
      const json = await res.json().catch(() => null);
      if (!res.ok || !json?.ok) {
        throw new Error(String(json?.error || `Apply all failed (${res.status})`));
      }

      const results = Array.isArray(json?.results) ? (json.results as any[]) : [];
      const cleaned = results
        .map((r) => ({
          name: String(r?.name || ''),
          status: String(r?.status || ''),
          folderName: r?.folderName ? String(r.folderName) : undefined,
          message: r?.message ? String(r.message) : undefined,
          error: r?.error ? String(r.error) : undefined,
        }))
        .filter((r) => r.name && r.status);
      setApplyAllResults(cleaned);
      setApplyAllTotal(cleaned.length);
      setApplyAllDone(cleaned.length);
      let a = 0;
      let s = 0;
      let f = 0;
      cleaned.forEach((r) => {
        if (r.status === 'applied') a += 1;
        else if (r.status === 'skipped_exists') s += 1;
        else if (r.status === 'failed') f += 1;
      });
      setApplyAllApplied(a);
      setApplyAllSkipped(s);
      setApplyAllFailed(f);
      setApplyAllResultsOpen(true);
      if (f > 0) toast.error(`Applied with ${f} failure(s)`, { id });
      else toast.success('All schemas applied', { id });
      await fetchOverview();
    } catch (e: any) {
      toast.error(e?.message || 'Apply all failed', { id });
    } finally {
      setApplyAllRunning(false);
      setApplyAllCurrent('');
      setApplyAllProgressOpen(false);
    }
  }, [applyAllRunning, fetchOverview]);

  const fetchMgmtConfig = React.useCallback(async () => {
    setLoadingMgmt(true);
    try {
      const token = await AdminKeyService.getSecret();
      if (!token) {
        throw new Error('Missing admin key');
      }
      const res = await fetch(API_ENDPOINT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Auth-Token': token,
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ action: 'supabase_mgmt_get', token }),
      });
      const json = await res.json().catch(() => null);
      if (!res.ok || !json?.ok) {
        throw new Error(json?.error || `Failed to load management config (${res.status})`);
      }
      const cfg = json?.management && typeof json.management === 'object' ? json.management : null;
      setMgmtCfg(cfg);
      const ref = cfg?.projectRef ? String(cfg.projectRef) : '';
      setMgmtProjectRef(ref);
      setMgmtRefAuto(ref.trim() === '');
      setMgmtToken('');
    } catch (e: any) {
      toast.error(e?.message || 'Failed to load management config');
    } finally {
      setLoadingMgmt(false);
    }
  }, []);

  React.useEffect(() => {
    if (!mgmtRefAuto) return;
    if ((mgmtProjectRef || '').trim() !== '') return;
    const derived = extractSupabaseProjectRef(supabaseUrl);
    if (derived) {
      setMgmtProjectRef(derived);
    }
  }, [mgmtProjectRef, mgmtRefAuto, supabaseUrl]);

  const saveMgmtConfig = React.useCallback(async (payload: { projectRef: string; accessToken?: string }) => {
    setSavingMgmt(true);
    try {
      const token = await AdminKeyService.getSecret();
      if (!token) {
        throw new Error('Missing admin key');
      }
      const res = await fetch(API_ENDPOINT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Auth-Token': token,
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          action: 'supabase_mgmt_set',
          projectRef: payload.projectRef,
          accessToken: payload.accessToken,
          token,
        }),
      });
      const json = await res.json().catch(() => null);
      if (!res.ok || !json?.ok) {
        throw new Error(json?.error || `Failed to save management config (${res.status})`);
      }
      const cfg = json?.management && typeof json.management === 'object' ? json.management : null;
      setMgmtCfg(cfg);
      setMgmtToken('');
    } finally {
      setSavingMgmt(false);
    }
  }, []);

  const fetchSupabaseConnection = React.useCallback(async () => {
    setLoadingSupabase(true);
    try {
      const token = await AdminKeyService.getSecret();
      if (!token) {
        throw new Error('Missing admin key');
      }
      const res = await fetch(API_ENDPOINT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Auth-Token': token,
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ action: 'supabase_get', token }),
      });
      const json = await res.json().catch(() => null);
      if (!res.ok || !json?.ok) {
        throw new Error(json?.error || `Failed to load Supabase connection (${res.status})`);
      }
      const c = json?.supabase;
      const parsed: SupabaseConnection = (c && typeof c === 'object') ? c : null;
      setSupabaseConn(parsed);

      setSupabaseUrl(parsed?.url ? String(parsed.url) : '');
      setSupabaseAnonKey(parsed?.anonKey ? String(parsed.anonKey) : '');
    } catch (e: any) {
      toast.error(e?.message || 'Failed to load Supabase connection');
    } finally {
      setLoadingSupabase(false);
    }
  }, []);

  const fetchServiceRole = React.useCallback(async () => {
    setLoadingServiceRole(true);
    try {
      const token = await AdminKeyService.getSecret();
      if (!token) throw new Error('Missing admin key');
      const res = await fetch(API_ENDPOINT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Auth-Token': token,
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ action: 'supabase_service_role_get', token }),
      });
      const json = await res.json().catch(() => null);
      if (!res.ok || !json?.ok) {
        throw new Error(json?.error || `Failed to load service role config (${res.status})`);
      }
      const cfg = json?.serviceRole && typeof json.serviceRole === 'object' ? json.serviceRole : null;
      setServiceRoleCfg(cfg ? { hasServiceRole: true, createdAt: cfg?.createdAt, updatedAt: cfg?.updatedAt } : null);
      setServiceRoleKeyInput('');
    } catch (e: any) {
      toast.error(e?.message || 'Failed to load service role config');
    } finally {
      setLoadingServiceRole(false);
    }
  }, []);

  const saveServiceRole = React.useCallback(async (serviceRoleKey: string) => {
    setSavingServiceRole(true);
    try {
      const token = await AdminKeyService.getSecret();
      if (!token) throw new Error('Missing admin key');
      const res = await fetch(API_ENDPOINT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Auth-Token': token,
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ action: 'supabase_service_role_set', token, serviceRoleKey }),
      });
      const json = await res.json().catch(() => null);
      if (!res.ok || !json?.ok) {
        throw new Error(json?.error || `Failed to save service role config (${res.status})`);
      }
      const cfg = json?.serviceRole && typeof json.serviceRole === 'object' ? json.serviceRole : null;
      setServiceRoleCfg(cfg ? { hasServiceRole: true, createdAt: cfg?.createdAt, updatedAt: cfg?.updatedAt } : null);
      setServiceRoleKeyInput('');
    } finally {
      setSavingServiceRole(false);
    }
  }, []);

  const fetchPostgresConfig = React.useCallback(async () => {
    setLoadingPg(true);
    try {
      const token = await AdminKeyService.getSecret();
      if (!token) {
        throw new Error('Missing admin key');
      }
      const res = await fetch(API_ENDPOINT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Auth-Token': token,
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ action: 'supabase_pg_get', token }),
      });
      const json = await res.json().catch(() => null);
      if (!res.ok || !json?.ok) {
        throw new Error(json?.error || `Failed to load Postgres config (${res.status})`);
      }
      const cfg = json?.postgres && typeof json.postgres === 'object' ? json.postgres : null;
      setPgCfg(cfg);
      setPgHost(cfg?.host ? String(cfg.host) : '');
      setPgPort(cfg?.port != null ? String(cfg.port) : '5432');
      setPgDatabase(cfg?.database ? String(cfg.database) : '');
      setPgUser(cfg?.user ? String(cfg.user) : '');
      setPgSslMode(cfg?.sslmode ? String(cfg.sslmode) : 'require');
      setPgPassword('');
      setPgConnectionString('');
    } catch (e: any) {
      toast.error(e?.message || 'Failed to load Postgres config');
    } finally {
      setLoadingPg(false);
    }
  }, []);

  const savePostgresConfig = React.useCallback(async (payload: {
    host: string;
    port: number;
    database: string;
    user: string;
    password?: string;
    sslmode: string;
  }) => {
    setSavingPg(true);
    try {
      const token = await AdminKeyService.getSecret();
      if (!token) {
        throw new Error('Missing admin key');
      }
      const res = await fetch(API_ENDPOINT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Auth-Token': token,
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          action: 'supabase_pg_set',
          host: payload.host,
          port: payload.port,
          database: payload.database,
          user: payload.user,
          password: payload.password,
          sslmode: payload.sslmode,
          token,
        }),
      });
      const json = await res.json().catch(() => null);
      if (!res.ok || !json?.ok) {
        throw new Error(json?.error || `Failed to save Postgres config (${res.status})`);
      }
      const cfg = json?.postgres && typeof json.postgres === 'object' ? json.postgres : null;
      setPgCfg(cfg);
      setPgPassword('');
    } finally {
      setSavingPg(false);
    }
  }, []);

  const testPostgresConfig = React.useCallback(async () => {
    setTestingPg(true);
    try {
      const token = await AdminKeyService.getSecret();
      if (!token) {
        throw new Error('Missing admin key');
      }
      const res = await fetch(API_ENDPOINT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Auth-Token': token,
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ action: 'supabase_pg_test', token }),
      });
      const json = await res.json().catch(() => null);
      if (!res.ok || !json?.ok) {
        throw new Error(json?.error || `Test failed (${res.status})`);
      }
      toast.success('Postgres connection OK');
    } finally {
      setTestingPg(false);
    }
  }, []);

  React.useEffect(() => {
    fetchAll();
    fetchSupabaseConnection();
    fetchServiceRole();
    fetchPostgresConfig();
    fetchMgmtConfig();
    fetchOverview();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openCreate = (provider: ProviderId) => {
    setEditProvider(provider);
    setEditId(null);
    setEditName('');
    setEditKey('');
    setEditOpen(true);
  };

  const openEdit = (provider: ProviderId, rec: ApiKeyRecord) => {
    setEditProvider(provider);
    setEditId(rec.id);
    setEditName(rec.name || '');
    setEditKey(rec.key || '');
    setEditOpen(true);
  };

  const closeEdit = () => {
    setEditOpen(false);
  };

  const saveKey = async () => {
    const name = (editName || '').trim();
    const key = (editKey || '').trim();
    if (!name) {
      toast.error('Please enter a name');
      return;
    }
    if (!key) {
      toast.error('Please enter an API key');
      return;
    }

    setSaving(true);
    const loadingId = toast.loading(editId ? 'Updating key...' : 'Creating key...');
    try {
      let token = await AdminKeyService.getSecret();
      if (!token) {
        toast.error('Missing admin key');
        toast.dismiss(loadingId);
        setSaving(false);
        return;
      }

      const res = await fetch(API_ENDPOINT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Auth-Token': token,
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          provider: editProvider,
          id: editId,
          name,
          key,
          token,
        }),
      });

      const json = await res.json().catch(() => null);
      if (!res.ok || !json?.ok) {
        throw new Error(json?.error || `Save failed (${res.status})`);
      }

      setKeysByProvider((prev) => ({
        ...prev,
        [editProvider]: Array.isArray(json?.providers?.[editProvider]) ? json.providers[editProvider] : prev[editProvider],
      }));
      toast.success('Saved', { id: loadingId });
      setEditOpen(false);
    } catch (e: any) {
      toast.error(e?.message || 'Failed to save', { id: loadingId });
    } finally {
      setSaving(false);
    }
  };

  const deleteKey = async (provider: ProviderId, id: string) => {
    setDeleting(true);
    const loadingId = toast.loading('Deleting key...');
    try {
      let token = await AdminKeyService.getSecret();
      if (!token) {
        toast.error('Missing admin key');
        toast.dismiss(loadingId);
        setDeleting(false);
        return;
      }

      const res = await fetch(API_ENDPOINT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Auth-Token': token,
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          action: 'delete',
          provider,
          id,
          token,
        }),
      });

      const json = await res.json().catch(() => null);
      if (!res.ok || !json?.ok) {
        throw new Error(json?.error || `Delete failed (${res.status})`);
      }

      setKeysByProvider((prev) => ({
        ...prev,
        [provider]: Array.isArray(json?.providers?.[provider]) ? json.providers[provider] : prev[provider],
      }));
      toast.success('Deleted', { id: loadingId });
    } catch (e: any) {
      toast.error(e?.message || 'Failed to delete', { id: loadingId });
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Box>
      {!!storageBackupJob?.jobId && (
        <Box
          ref={storageBackupPopupRef}
          sx={{
            position: 'fixed',
            left: storageBackupPos?.x ?? 16,
            top: storageBackupPos?.y ?? 16,
            zIndex: 1400,
            width: storageBackupMinimized ? 340 : 420,
            maxWidth: 'calc(100vw - 32px)',
          }}
        >
          <Card sx={{ borderRadius: 3, overflow: 'hidden', border: '1px solid', borderColor: 'divider', boxShadow: '0 10px 30px rgba(0,0,0,0.12)' }}>
            <CardContent sx={{ p: 2 }}>
              {(() => {
                const job = storageBackupJob;
                const bucketsArr = Array.isArray(job?.buckets) ? job!.buckets : [];
                const totalBuckets = Number(job?.totalBuckets || bucketsArr.length || 0);
                const bucketIndex = Math.max(0, Math.min(Number(job?.bucketIndex || 0), Math.max(0, bucketsArr.length - 1)));
                const currentBucket = bucketsArr.length ? bucketsArr[bucketIndex] : null;
                const currentDone = Number(currentBucket?.done || 0);
                const currentTotal = Number(currentBucket?.total || 0);
                const totalDone = Number(job?.written || 0) + Number(job?.failed || 0);
                const totalObjs = Number(job?.totalObjects || 0);
                const overallPct = totalObjs > 0 ? Math.min(100, Math.round((totalDone / totalObjs) * 100)) : 0;
                const curPct = currentTotal > 0 ? Math.min(100, Math.round((currentDone / currentTotal) * 100)) : 0;
                const isRunning = job?.status === 'running';
                const isPaused = job?.status === 'paused';
                const isDone = job?.status === 'done';
                const isCanceled = job?.status === 'canceled';
                const isError = job?.status === 'error';
                const statusLabel = isRunning
                  ? 'Downloading'
                  : isPaused
                    ? 'Paused'
                    : isDone
                      ? 'Done'
                      : isCanceled
                        ? 'Canceled'
                        : 'Error';

                return (
                  <>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2 }}>
                      <Box
                        sx={{ minWidth: 0, cursor: 'grab', userSelect: 'none', flex: 1 }}
                        onPointerDown={(e) => {
                          if (e.button !== 0) return;
                          const rect = storageBackupPopupRef.current?.getBoundingClientRect();
                          const px = storageBackupPos?.x ?? (rect?.left ?? 16);
                          const py = storageBackupPos?.y ?? (rect?.top ?? 16);
                          storageBackupDragActive.current = true;
                          storageBackupDragOffset.current = { dx: e.clientX - px, dy: e.clientY - py };
                          try {
                            (e.target as HTMLElement | null)?.setPointerCapture?.(e.pointerId);
                          } catch (_) {
                          }
                        }}
                      >
                        <Typography fontWeight={900} noWrap>Storage buckets backup</Typography>
                        <Typography variant="body2" color="text.secondary" noWrap>
                          {statusLabel} · {totalDone}/{totalObjs} files
                        </Typography>
                      </Box>

                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Tooltip title={storageBackupMinimized ? 'Expand' : 'Minimize'}>
                          <IconButton size="small" onClick={() => setStorageBackupMinimized((p) => !p)}>
                            {storageBackupMinimized ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                          </IconButton>
                        </Tooltip>

                        <Tooltip title={isRunning ? 'Pause' : 'Resume'}>
                          <span>
                            <IconButton
                              size="small"
                              onClick={() => (isRunning ? pauseStorageBackup() : resumeStorageBackup())}
                              disabled={isDone || isCanceled || isError}
                            >
                              {isRunning ? <PauseIcon fontSize="small" /> : <PlayArrowIcon fontSize="small" />}
                            </IconButton>
                          </span>
                        </Tooltip>

                        <Tooltip title="Cancel">
                          <span>
                            <IconButton
                              size="small"
                              color="error"
                              onClick={cancelStorageBackup}
                              disabled={isDone || isCanceled || isError}
                            >
                              <CloseIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>

                        <Tooltip title="Close">
                          <IconButton size="small" onClick={closeStorageBackupPopup}>
                            <CloseIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </Box>

                    <Box sx={{ mt: 1.5 }}>
                      <LinearProgress variant="determinate" value={overallPct} sx={{ height: 10, borderRadius: 999 }} />
                      {!storageBackupMinimized && (
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.75 }}>
                          <Typography variant="caption" color="text.secondary">
                            Buckets {Math.min(totalBuckets, bucketIndex + 1)}/{totalBuckets}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">{overallPct}%</Typography>
                        </Box>
                      )}
                    </Box>

                    {!storageBackupMinimized && (
                      <Box sx={{ mt: 2 }}>
                        <Typography variant="caption" color="text.secondary">Current bucket</Typography>
                        <Typography fontWeight={800} sx={{ fontFamily: 'monospace' }}>
                          {String(currentBucket?.name || '-')}
                        </Typography>
                        <Box sx={{ mt: 1 }}>
                          <LinearProgress variant="determinate" value={curPct} sx={{ height: 8, borderRadius: 999 }} />
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.75 }}>
                            <Typography variant="caption" color="text.secondary">
                              {currentDone}/{currentTotal} files
                            </Typography>
                            <Typography variant="caption" color="text.secondary">{curPct}%</Typography>
                          </Box>
                        </Box>

                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1.5 }}>
                          <Typography variant="caption" color="text.secondary">Written {job?.written || 0}</Typography>
                          <Typography variant="caption" color={job?.failed ? 'warning.main' : 'text.secondary'}>
                            Failed {job?.failed || 0}
                          </Typography>
                        </Box>

                        {job?.status === 'error' && job?.message ? (
                          <Typography variant="body2" color="error" sx={{ mt: 1 }}>
                            {String(job.message)}
                          </Typography>
                        ) : null}
                      </Box>
                    )}
                  </>
                );
              })()}
            </CardContent>
          </Card>
        </Box>
      )}

      <Card sx={{ mb: 3, borderRadius: 3, overflow: 'hidden' }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
            <Box>
              <Typography variant="h5" fontWeight={900} gutterBottom>
                Supabase Connection
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Stored at storage/integrations/supabase/connection.json
              </Typography>
            </Box>
            <Chip
              color={supabaseConn ? 'success' : 'default'}
              label={supabaseConn ? 'Saved' : 'Not saved'}
              sx={{ fontWeight: 800 }}
            />
          </Box>

          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2, mt: 2 }}>
            <TextField
              label="Project URL"
              value={supabaseUrl}
              onChange={(e) => setSupabaseUrl(e.target.value)}
              placeholder="https://xxxx.supabase.co"
              fullWidth
            />
            <TextField
              label="Public anon key"
              value={supabaseAnonKey}
              onChange={(e) => setSupabaseAnonKey(e.target.value)}
              placeholder="eyJhbGciOi..."
              fullWidth
              multiline
              minRows={2}
            />
          </Box>

          <Box sx={{ mt: 2, p: 2, borderRadius: 2, border: '1px solid', borderColor: 'divider', bgcolor: 'rgba(2,132,199,0.03)' }}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
              <Box>
                <Typography fontWeight={900}>Service role key (for private bucket downloads)</Typography>
                <Typography variant="body2" color="text.secondary">
                  Stored server-side at storage/integrations/supabase/service_role.json (write-only).
                </Typography>
              </Box>
              <Chip
                color={serviceRoleCfg?.hasServiceRole ? 'success' : 'default'}
                label={serviceRoleCfg?.hasServiceRole ? 'Configured' : 'Not configured'}
                sx={{ fontWeight: 800 }}
              />
            </Box>

            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr' }, gap: 2, mt: 2 }}>
              <TextField
                label={serviceRoleCfg?.hasServiceRole ? 'Service role key (saved — leave blank to keep)' : 'Service role key'}
                value={serviceRoleKeyInput}
                onChange={(e) => setServiceRoleKeyInput(e.target.value)}
                type="password"
                fullWidth
                multiline
                minRows={2}
              />
            </Box>

            <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, mt: 2, flexWrap: 'wrap' }}>
              <Button
                variant="text"
                color="error"
                onClick={async () => {
                  const id = toast.loading('Disabling service role key...');
                  try {
                    await saveServiceRole('');
                    toast.success('Disabled', { id });
                    await fetchServiceRole();
                  } catch (e: any) {
                    toast.error(e?.message || 'Failed', { id });
                  }
                }}
                disabled={savingServiceRole || loadingServiceRole}
                sx={{ textTransform: 'none' }}
              >
                Disable
              </Button>

              <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, flexWrap: 'wrap' }}>
                <Button
                  variant="outlined"
                  onClick={fetchServiceRole}
                  disabled={loadingServiceRole || savingServiceRole}
                  sx={{ textTransform: 'none' }}
                >
                  {loadingServiceRole ? <CircularProgress size={18} /> : 'Reload'}
                </Button>
                <Button
                  variant="contained"
                  onClick={async () => {
                    const k = (serviceRoleKeyInput || '').trim();
                    if (!k && !serviceRoleCfg?.hasServiceRole) {
                      toast.error('Please enter a service role key');
                      return;
                    }
                    const id = toast.loading('Saving service role key...');
                    try {
                      await saveServiceRole(k);
                      toast.success('Saved', { id });
                      await fetchServiceRole();
                    } catch (e: any) {
                      toast.error(e?.message || 'Failed to save', { id });
                    }
                  }}
                  disabled={savingServiceRole}
                  sx={{ textTransform: 'none' }}
                >
                  {savingServiceRole ? <CircularProgress size={18} /> : 'Save'}
                </Button>
              </Box>
            </Box>
          </Box>

          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 2 }}>
            <Button
              variant="outlined"
              onClick={fetchSupabaseConnection}
              disabled={loadingSupabase || savingSupabase}
              sx={{ textTransform: 'none' }}
            >
              {loadingSupabase ? <CircularProgress size={18} /> : 'Reload'}
            </Button>
            <Button
              variant="contained"
              onClick={async () => {
                const u = (supabaseUrl || '').trim();
                const k = (supabaseAnonKey || '').trim();
                if (!u || !k) {
                  toast.error('Please enter both Project URL and anon key');
                  return;
                }
                const id = toast.loading('Saving Supabase connection...');
                try {
                  await saveSupabaseConnection(u, k);
                  toast.success('Supabase connection saved', { id });
                } catch (e: any) {
                  toast.error(e?.message || 'Failed to save', { id });
                }
              }}
              disabled={savingSupabase}
              sx={{ textTransform: 'none' }}
            >
              {savingSupabase ? <CircularProgress size={18} /> : 'Save'}
            </Button>
          </Box>

          <Box sx={{ mt: 3, pt: 3, borderTop: '1px solid', borderColor: 'divider' }}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
              <Box>
                <Typography variant="h6" fontWeight={900} gutterBottom>
                  Server-side Postgres (Admin)
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Optional. Stored at storage/integrations/supabase/postgres.json. Never exposed to the browser.
                </Typography>
              </Box>
              <Chip
                color={pgCfg ? 'success' : 'default'}
                label={pgCfg ? 'Configured' : 'Not configured'}
                sx={{ fontWeight: 800 }}
              />
            </Box>

            <Box sx={{ mt: 2 }}>
              <TextField
                label="Connection string (URI)"
                value={pgConnectionString}
                onChange={(e) => setPgConnectionString(e.target.value)}
                placeholder="postgresql://postgres:YOUR_PASSWORD@db.<project-ref>.supabase.co:5432/postgres?sslmode=require"
                fullWidth
                multiline
                minRows={2}
              />
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
                <Button
                  variant="outlined"
                  onClick={() => {
                    const parsed = parsePostgresConnectionString(pgConnectionString);
                    if (!parsed) {
                      toast.error('Invalid connection string');
                      return;
                    }
                    setPgHost(parsed.host);
                    setPgPort(String(parsed.port || 5432));
                    setPgDatabase(parsed.database || '');
                    setPgUser(parsed.user || '');
                    if (parsed.sslmode) setPgSslMode(String(parsed.sslmode));
                    if (parsed.password) {
                      const pw = String(parsed.password || '').trim();
                      const pwl = pw.toLowerCase();
                      if (pw && pwl !== '[your-password]' && pwl !== 'your-password' && pwl !== '<your-password>') {
                        setPgPassword(pw);
                      }
                    }
                    toast.success('Parsed');
                  }}
                  sx={{ textTransform: 'none' }}
                >
                  Parse
                </Button>
              </Box>
            </Box>

            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '2fr 1fr' }, gap: 2, mt: 2 }}>
              <TextField
                label="Host"
                value={pgHost}
                onChange={(e) => setPgHost(e.target.value)}
                placeholder="db.<project-ref>.supabase.co"
                fullWidth
              />
              <TextField
                label="Port"
                value={pgPort}
                onChange={(e) => setPgPort(e.target.value)}
                placeholder="5432"
                fullWidth
              />
            </Box>

            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2, mt: 2 }}>
              <TextField
                label="Database"
                value={pgDatabase}
                onChange={(e) => setPgDatabase(e.target.value)}
                placeholder="postgres"
                fullWidth
              />
              <TextField
                label="User"
                value={pgUser}
                onChange={(e) => setPgUser(e.target.value)}
                placeholder="postgres"
                fullWidth
              />
            </Box>

            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2, mt: 2 }}>
              <TextField
                label={pgCfg?.hasPassword ? 'Password (saved — leave blank to keep)' : 'Password'}
                value={pgPassword}
                onChange={(e) => setPgPassword(e.target.value)}
                type="password"
                fullWidth
              />
              <TextField
                label="SSL mode"
                value={pgSslMode}
                onChange={(e) => setPgSslMode(e.target.value)}
                placeholder="require"
                fullWidth
              />
            </Box>

            <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, mt: 2, flexWrap: 'wrap' }}>
              <Button
                variant="text"
                color="error"
                onClick={async () => {
                  const id = toast.loading('Disabling Postgres config...');
                  try {
                    await savePostgresConfig({ host: '', port: 5432, database: '', user: '', password: '', sslmode: pgSslMode || 'require' });
                    setPgHost('');
                    setPgPort('5432');
                    setPgDatabase('');
                    setPgUser('');
                    setPgPassword('');
                    setPgCfg(null);
                    toast.success('Disabled', { id });
                  } catch (e: any) {
                    toast.error(e?.message || 'Failed', { id });
                  }
                }}
                disabled={savingPg}
                sx={{ textTransform: 'none' }}
              >
                Disable
              </Button>

              <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, flexWrap: 'wrap' }}>
                <Button
                  variant="outlined"
                  onClick={fetchPostgresConfig}
                  disabled={loadingPg || savingPg || testingPg}
                  sx={{ textTransform: 'none' }}
                >
                  {loadingPg ? <CircularProgress size={18} /> : 'Reload'}
                </Button>
                <Button
                  variant="outlined"
                  onClick={async () => {
                    const id = toast.loading('Testing Postgres connection...');
                    try {
                      await testPostgresConfig();
                      toast.success('Connection OK', { id });
                    } catch (e: any) {
                      const msg = e?.message || 'Test failed';
                      toast.error(msg, { id });
                      setPgHelp(buildPostgresHelp(msg, pgHost));
                    }
                  }}
                  disabled={testingPg || savingPg}
                  sx={{ textTransform: 'none' }}
                >
                  {testingPg ? <CircularProgress size={18} /> : 'Test connection'}
                </Button>
                <Button
                  variant="contained"
                  onClick={async () => {
                    const host = (pgHost || '').trim();
                    const database = (pgDatabase || '').trim();
                    const user = (pgUser || '').trim();
                    const port = parseInt(String(pgPort || '5432'), 10);
                    const sslmode = (pgSslMode || 'require').trim();
                    if (!host || !database || !user) {
                      toast.error('Host, Database, and User are required');
                      return;
                    }
                    if (!Number.isFinite(port) || port <= 0) {
                      toast.error('Invalid port');
                      return;
                    }

                    const pw = (pgPassword || '').trim();
                    const hasSavedPassword = !!pgCfg?.hasPassword;
                    if (!pw && !hasSavedPassword) {
                      setPgPendingSave({ host, port, database, user, sslmode });
                      setPgPasswordPromptValue('');
                      setPgPasswordPromptOpen(true);
                      return;
                    }

                    const id = toast.loading('Saving Postgres config...');
                    try {
                      await savePostgresConfig({
                        host,
                        port,
                        database,
                        user,
                        password: pw,
                        sslmode,
                      });
                      toast.success('Saved', { id });
                    } catch (e: any) {
                      toast.error(e?.message || 'Failed to save', { id });
                    }
                  }}
                  disabled={savingPg}
                  sx={{ textTransform: 'none' }}
                >
                  {savingPg ? <CircularProgress size={18} /> : 'Save'}
                </Button>
              </Box>
            </Box>
          </Box>

          <Box sx={{ mt: 3, pt: 3, borderTop: '1px solid', borderColor: 'divider' }}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
              <Box>
                <Typography variant="h6" fontWeight={900} gutterBottom>
                  Supabase Project Overview
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Tables are read via Postgres admin connection. Edge Functions require Supabase Management API credentials.
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                <Chip
                  color={(overview?.tablesError || !Array.isArray(overview?.tables)) ? 'default' : 'success'}
                  label={`Tables: ${Array.isArray(overview?.tables) ? overview!.tables!.length : 0}`}
                  sx={{ fontWeight: 800 }}
                />
                <Chip
                  color={(overview?.bucketsError || !Array.isArray(overview?.buckets)) ? 'default' : 'success'}
                  label={`Buckets: ${Array.isArray(overview?.buckets) ? overview!.buckets!.length : 0}`}
                  sx={{ fontWeight: 800 }}
                />
                <Chip
                  color={Array.isArray(overview?.functions) ? 'success' : 'default'}
                  label={`Edge Functions: ${Array.isArray(overview?.functions) ? overview!.functions!.length : 0}`}
                  sx={{ fontWeight: 800 }}
                />
              </Box>
            </Box>

            <Card sx={{ mt: 2, borderRadius: 3, overflow: 'hidden', border: '1px solid', borderColor: 'divider', boxShadow: 'none' }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
                  <Box>
                    <Typography fontWeight={900}>Management API (optional)</Typography>
                    <Typography variant="body2" color="text.secondary">
                      Store Project Ref + Personal Access Token server-side (token is write-only).
                    </Typography>
                  </Box>
                  <Chip
                    color={mgmtCfg?.hasToken ? 'success' : 'default'}
                    label={mgmtCfg?.hasToken ? 'Token saved' : 'No token'}
                    sx={{ fontWeight: 800 }}
                  />
                </Box>

                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2, mt: 2 }}>
                  <TextField
                    label="Project ref"
                    value={mgmtProjectRef}
                    onChange={(e) => {
                      const next = e.target.value;
                      const derived = extractSupabaseProjectRef(next);
                      if (derived) {
                        setMgmtProjectRef(derived);
                        setMgmtRefAuto(false);
                        return;
                      }
                      setMgmtProjectRef(next);
                      setMgmtRefAuto(next.trim() === '');
                    }}
                    placeholder="hsfggdxlmlodoxrjsmdr"
                    fullWidth
                  />
                  <TextField
                    label={mgmtCfg?.hasToken ? 'Personal access token (saved — leave blank to keep)' : 'Personal access token'}
                    value={mgmtToken}
                    onChange={(e) => setMgmtToken(e.target.value)}
                    type="password"
                    fullWidth
                  />
                </Box>

                <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, mt: 2, flexWrap: 'wrap' }}>
                  <Button
                    variant="text"
                    color="error"
                    onClick={async () => {
                      const id = toast.loading('Disabling Management API config...');
                      try {
                        await saveMgmtConfig({ projectRef: '', accessToken: '' });
                        setMgmtProjectRef('');
                        setMgmtToken('');
                        setMgmtCfg(null);
                        toast.success('Disabled', { id });
                      } catch (e: any) {
                        toast.error(e?.message || 'Failed', { id });
                      }
                    }}
                    disabled={savingMgmt}
                    sx={{ textTransform: 'none' }}
                  >
                    Disable
                  </Button>

                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, flexWrap: 'wrap' }}>
                    <Button
                      variant="outlined"
                      onClick={fetchMgmtConfig}
                      disabled={loadingMgmt || savingMgmt}
                      sx={{ textTransform: 'none' }}
                    >
                      {loadingMgmt ? <CircularProgress size={18} /> : 'Reload'}
                    </Button>
                    <Button
                      variant="contained"
                      onClick={async () => {
                        const ref = (mgmtProjectRef || '').trim();
                        const at = (mgmtToken || '').trim();
                        const derived = extractSupabaseProjectRef(ref) || extractSupabaseProjectRef(supabaseUrl);
                        if (!derived) {
                          toast.error('Project ref is required (or provide a Supabase Project URL like https://<ref>.supabase.co)');
                          return;
                        }
                        const id = toast.loading('Saving Management API config...');
                        try {
                          await saveMgmtConfig({ projectRef: derived, accessToken: at });
                          setMgmtProjectRef(derived);
                          await fetchOverview();
                          toast.success('Saved', { id });
                        } catch (e: any) {
                          toast.error(e?.message || 'Failed to save', { id });
                        }
                      }}
                      disabled={savingMgmt}
                      sx={{ textTransform: 'none' }}
                    >
                      {savingMgmt ? <CircularProgress size={18} /> : 'Save'}
                    </Button>
                  </Box>
                </Box>
              </CardContent>
            </Card>

            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap', mt: 2 }}>
              <TextField
                label="Search tables / functions"
                value={overviewQuery}
                onChange={(e) => setOverviewQuery(e.target.value)}
                placeholder="e.g. profiles, orders, hello-world"
                fullWidth
                sx={{ maxWidth: 520 }}
              />
              <Button
                variant="contained"
                onClick={fetchOverview}
                disabled={loadingOverview}
                sx={{ textTransform: 'none', borderRadius: 999, px: 2.5, fontWeight: 900 }}
                startIcon={<RefreshIcon />}
              >
                {loadingOverview ? <CircularProgress size={18} /> : 'Refresh overview'}
              </Button>
            </Box>

            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '2fr 1fr' }, gap: 2, mt: 2 }}>
              <Card sx={{ borderRadius: 3, overflow: 'hidden', border: '1px solid', borderColor: 'divider', boxShadow: 'none' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
                    <Typography fontWeight={900}>Tables</Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                      <Tabs
                        value={tablesSource}
                        onChange={(_, v) => {
                          const next = v === 'backups' ? 'backups' : 'supabase';
                          setTablesSource(next);
                          if (next === 'supabase') {
                            fetchOverview();
                          } else {
                            fetchTableBackupsLocal(false);
                          }
                        }}
                        sx={{ minHeight: 32 }}
                        TabIndicatorProps={{ style: { height: 3, borderRadius: 999 } }}
                      >
                        <Tab value="supabase" label="Supabase" sx={{ textTransform: 'none', fontWeight: 900, minHeight: 32 }} />
                        <Tab value="backups" label="Backups" sx={{ textTransform: 'none', fontWeight: 900, minHeight: 32 }} />
                      </Tabs>

                      {tablesSource === 'supabase' && Array.isArray(overview?.tables) && overview!.tables!.length > 0 ? (
                        <Button
                          variant="contained"
                          size="small"
                          onClick={() => setTableBulkDialogOpen(true)}
                          disabled={tableBulkRunning}
                          startIcon={<DownloadIcon />}
                          sx={{ textTransform: 'none', borderRadius: 999, fontWeight: 900 }}
                        >
                          Download all
                        </Button>
                      ) : null}
                      {tablesSource === 'backups' ? (
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={() => fetchTableBackupsLocal(true)}
                          disabled={loadingTableBackups}
                          startIcon={<RefreshIcon />}
                          sx={{ textTransform: 'none', borderRadius: 999, fontWeight: 900 }}
                        >
                          {loadingTableBackups ? <CircularProgress size={18} /> : 'Refresh backups'}
                        </Button>
                      ) : null}
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() => setApplyAllOpen(true)}
                        disabled={applyAllRunning || !!overview?.tablesError}
                        startIcon={<UploadIcon />}
                        sx={{ textTransform: 'none', borderRadius: 999, fontWeight: 900 }}
                      >
                        Upload all
                      </Button>
                      {overview?.tablesError ? (
                        <Chip color="warning" label="Needs Postgres admin connection" sx={{ fontWeight: 800 }} />
                      ) : (
                        <Chip color="success" label="Connected" sx={{ fontWeight: 800 }} />
                      )}
                    </Box>
                  </Box>

                  {!!overview?.tablesError && (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                      {String(overview.tablesError)}
                    </Typography>
                  )}

                  <TableContainer sx={{ mt: 2, borderRadius: 2, border: '1px solid', borderColor: 'divider', maxHeight: 420, overflowY: 'auto' }}>
                    <Table stickyHeader size="small" sx={{ minWidth: 860 }}>
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 900 }}>Schema</TableCell>
                          <TableCell sx={{ fontWeight: 900 }}>Name</TableCell>
                          <TableCell sx={{ fontWeight: 900 }}>Type</TableCell>
                          <TableCell sx={{ fontWeight: 900 }} align="right">Rows</TableCell>
                          <TableCell sx={{ fontWeight: 900 }} align="right">Size</TableCell>
                          <TableCell sx={{ fontWeight: 900 }} align="right">Actions</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {(() => {
                          const q = (overviewQuery || '').trim().toLowerCase();
                          type DisplayTableRow = {
                            schema: string;
                            name: string;
                            kind: string;
                            approxRows?: number | null;
                            totalBytes?: number | null;
                            __folderName?: string;
                            __updatedAt?: string | null;
                          };

                          let rows: DisplayTableRow[] = [];
                          if (tablesSource === 'supabase') {
                            rows = (Array.isArray(overview?.tables) ? overview!.tables! : []).map((t) => ({
                              schema: t.schema,
                              name: t.name,
                              kind: t.kind,
                              approxRows: t.approxRows,
                              totalBytes: t.totalBytes,
                            }));
                          } else {
                            rows = tableLocalBackups.map((b) => ({
                              schema: b.schema,
                              name: b.table,
                              kind: 'backup',
                              approxRows: null,
                              totalBytes: null,
                              __folderName: b.folderName,
                              __updatedAt: b.updatedAt ?? null,
                            }));
                          }
                          rows = rows.filter((x: DisplayTableRow) => !!x.schema && !!x.name);
                          const filtered = q
                            ? rows.filter((t: DisplayTableRow) =>
                                `${t.schema}.${t.name}`.toLowerCase().includes(q) ||
                                String(t.kind || '').toLowerCase().includes(q)
                              )
                            : rows;
                          if (filtered.length === 0) {
                            return (
                              <TableRow>
                                <TableCell colSpan={6}>
                                  <Typography variant="body2" color="text.secondary">
                                    {tablesSource === 'supabase' && overview?.tablesError
                                      ? 'Configure the Server-side Postgres connection (prefer a Supabase pooler URI) to list tables.'
                                      : tablesSource === 'backups'
                                        ? (loadingTableBackups ? 'Loading backups...' : 'No backups found.')
                                        : 'No tables found.'}
                                  </Typography>
                                </TableCell>
                              </TableRow>
                            );
                          }
                          return filtered.slice(0, 200).map((t: DisplayTableRow, idx: number) => (
                            (() => {
                              const key = `${t.schema}.${t.name}`;
                              const isBusy = !!tableRowBusy[key];
                              const canAct = !overview?.tablesError;
                              const folderName = t.__folderName ? String(t.__folderName) : '';
                              return (
                            <TableRow key={`${t.schema}.${t.name}.${idx}`} hover>
                              <TableCell>{t.schema}</TableCell>
                              <TableCell sx={{ fontFamily: 'monospace' }}>{t.name}</TableCell>
                              <TableCell>
                                <Chip size="small" label={t.kind} sx={{ fontWeight: 800 }} />
                              </TableCell>
                              <TableCell align="right">
                                {tablesSource === 'supabase' ? Number(t.approxRows || 0).toLocaleString() : '-'}
                              </TableCell>
                              <TableCell align="right">{tablesSource === 'supabase' ? formatBytes(Number(t.totalBytes || 0)) : '-'}</TableCell>
                              <TableCell align="right">
                                <Box sx={{ display: 'flex', gap: 0.75, justifyContent: 'flex-end' }}>
                                  {tablesSource === 'supabase' ? (
                                    <>
                                      <Tooltip title="Download schema.sql">
                                        <span>
                                          <IconButton
                                            size="small"
                                            disabled={!canAct || isBusy}
                                            onClick={() => downloadOneTableSchema(String(t.schema || ''), String(t.name || ''))}
                                            sx={{ border: '1px solid', borderColor: 'divider', bgcolor: 'rgba(255,255,255,0.8)' }}
                                          >
                                            <DownloadIcon fontSize="small" />
                                          </IconButton>
                                        </span>
                                      </Tooltip>
                                      <Tooltip title="Upload schema from backups">
                                        <span>
                                          <IconButton
                                            size="small"
                                            disabled={!canAct || isBusy}
                                            onClick={() => openTableUpload(String(t.schema || ''), String(t.name || ''))}
                                            sx={{ border: '1px solid', borderColor: 'divider', bgcolor: 'rgba(255,255,255,0.8)' }}
                                          >
                                            <UploadIcon fontSize="small" />
                                          </IconButton>
                                        </span>
                                      </Tooltip>
                                    </>
                                  ) : (
                                    <>
                                      <Tooltip title={folderName ? `Apply latest backup (${folderName})` : 'Apply latest backup'}>
                                        <span>
                                          <IconButton
                                            size="small"
                                            disabled={!canAct || isBusy || !folderName}
                                            onClick={() => applyLatestTableBackup(String(t.schema || ''), String(t.name || ''), folderName)}
                                            sx={{ border: '1px solid', borderColor: 'divider', bgcolor: 'rgba(255,255,255,0.8)' }}
                                          >
                                            <UploadIcon fontSize="small" />
                                          </IconButton>
                                        </span>
                                      </Tooltip>
                                    </>
                                  )}
                                </Box>
                              </TableCell>
                            </TableRow>
                              );
                            })()
                          ));
                        })()}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </CardContent>
              </Card>

              <Card sx={{ borderRadius: 3, overflow: 'hidden', border: '1px solid', borderColor: 'divider', boxShadow: 'none' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
                    <Typography fontWeight={900}>Edge Functions</Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                      <Tabs
                        value={edgeSource}
                        onChange={(_, v) => {
                          const next = v === 'backups' ? 'backups' : 'supabase';
                          setEdgeSource(next);
                          if (next === 'supabase') {
                            fetchOverview();
                          } else {
                            fetchEdgeBackupsLocal(false);
                          }
                        }}
                        sx={{ minHeight: 32 }}
                        TabIndicatorProps={{ style: { height: 3, borderRadius: 999 } }}
                      >
                        <Tab value="supabase" label="Supabase" sx={{ textTransform: 'none', fontWeight: 900, minHeight: 32 }} />
                        <Tab value="backups" label="Backups" sx={{ textTransform: 'none', fontWeight: 900, minHeight: 32 }} />
                      </Tabs>

                      {edgeSource === 'backups' ? (
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={() => fetchEdgeBackupsLocal(true)}
                          disabled={loadingEdgeBackups}
                          startIcon={<RefreshIcon />}
                          sx={{ textTransform: 'none', borderRadius: 999, fontWeight: 900 }}
                        >
                          {loadingEdgeBackups ? <CircularProgress size={18} /> : 'Refresh backups'}
                        </Button>
                      ) : null}

                      <Tooltip title="Deploy an Edge Function">
                        <span>
                          <IconButton
                            onClick={openUploadDialog}
                            size="small"
                            sx={{
                              border: '1px solid',
                              borderColor: 'divider',
                              bgcolor: 'rgba(255,255,255,0.8)',
                            }}
                          >
                            <UploadIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                      {edgeSource === 'supabase' && Array.isArray(overview?.functions) ? (
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={openBulkUploadDialog}
                          disabled={bulkUploadRunning}
                          startIcon={<UploadIcon />}
                          sx={{ textTransform: 'none', borderRadius: 999, fontWeight: 900 }}
                        >
                          Upload missing
                        </Button>
                      ) : null}
                      {edgeSource === 'supabase' && Array.isArray(overview?.functions) && overview!.functions!.length > 0 ? (
                        <Button
                          variant="contained"
                          size="small"
                          onClick={() => setBulkDialogOpen(true)}
                          disabled={bulkRunning}
                          startIcon={<DownloadIcon />}
                          sx={{ textTransform: 'none', borderRadius: 999, fontWeight: 900 }}
                        >
                          Download all
                        </Button>
                      ) : null}
                      {edgeSource === 'supabase' && mgmtCfg?.hasToken && !Array.isArray(overview?.functions) ? (
                        <Button
                          variant="contained"
                          size="small"
                          onClick={fetchOverview}
                          disabled={loadingOverview}
                          startIcon={<RefreshIcon />}
                          sx={{ textTransform: 'none', borderRadius: 999, fontWeight: 900 }}
                        >
                          {loadingOverview ? <CircularProgress size={18} /> : 'Fetch Edge Functions'}
                        </Button>
                      ) : null}
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() => setEdgeHelpOpen(true)}
                        startIcon={<HelpOutlineIcon />}
                        sx={{ textTransform: 'none', borderRadius: 999, fontWeight: 900 }}
                      >
                        How to enable
                      </Button>
                      {edgeSource === 'backups' ? (
                        <>
                          <Button
                            variant="contained"
                            color="error"
                            size="small"
                            onClick={openBulkDeleteBackupsDialog}
                            disabled={bulkDeleteBackupsRunning || loadingEdgeBackups}
                            startIcon={<DeleteIcon />}
                            sx={{ textTransform: 'none', borderRadius: 999, fontWeight: 900 }}
                          >
                            Delete
                          </Button>
                          <Chip color="info" label="From backups" sx={{ fontWeight: 800 }} />
                        </>
                      ) : Array.isArray(overview?.functions) ? (
                        <Chip color="success" label="Available" sx={{ fontWeight: 800 }} />
                      ) : (
                        <Chip color="default" label="Needs Management API" sx={{ fontWeight: 800 }} />
                      )}
                    </Box>
                  </Box>

                  {!!overview?.functionsError && (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                      {String(overview.functionsError)}
                    </Typography>
                  )}

                  <Box sx={{ mt: 2, display: 'grid', gap: 1, maxHeight: 420, overflowY: 'auto', pr: 0.5 }}>
                    {(() => {
                      const q = (overviewQuery || '').trim().toLowerCase();
                      const arr = edgeSource === 'supabase'
                        ? (Array.isArray(overview?.functions) ? overview!.functions! : [])
                        : edgeLocalBackups.map((b: any) => ({
                          name: b.name,
                          status: b.status,
                          __folder: b.folder,
                          __updatedAt: b.updatedAt,
                        }));
                      const filtered = q
                        ? arr.filter((f: any) => String(f?.name || '').toLowerCase().includes(q))
                        : arr;
                      if (edgeSource === 'supabase' && !Array.isArray(overview?.functions)) {
                        return (
                          <Typography variant="body2" color="text.secondary">
                            Save a Project ref + Personal Access Token above to list Edge Functions.
                          </Typography>
                        );
                      }
                      if (filtered.length === 0) {
                        return (
                          <Typography variant="body2" color="text.secondary">
                            {edgeSource === 'backups' ? (loadingEdgeBackups ? 'Loading backups...' : 'No backups found.') : 'No functions found.'}
                          </Typography>
                        );
                      }
                      return filtered.slice(0, 100).map((f: any, idx: number) => {
                        const name = String(f?.name || f?.slug || f?.id || `function-${idx}`);
                        const status = String(f?.status || f?.state || '').trim();
                        const isBusy = !!downloadingFn[name];
                        const isDeleting = !!deletingFn[name];
                        const isDeployingBackup = !!deployingBackupFn[name];
                        const canDeployBackup = edgeSource === 'backups' && status === 'ok';
                        return (
                          <Box
                            key={`${name}.${idx}`}
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                              gap: 2,
                              p: 1.25,
                              borderRadius: 2,
                              border: '1px solid',
                              borderColor: 'divider',
                              bgcolor: 'rgba(2,132,199,0.03)',
                            }}
                          >
                            <Box>
                              <Typography fontWeight={800} sx={{ fontFamily: 'monospace' }}>{name}</Typography>
                              {status ? (
                                <Typography variant="caption" color="text.secondary">{status}</Typography>
                              ) : null}
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              {edgeSource === 'backups' ? (
                                <>
                                  <Tooltip title="Deploy this Edge Function from server backup">
                                    <span>
                                      <IconButton
                                        onClick={() => deployEdgeFunctionFromBackup(name)}
                                        disabled={!canDeployBackup || isDeployingBackup}
                                        size="small"
                                        sx={{
                                          border: '1px solid',
                                          borderColor: 'divider',
                                          bgcolor: 'rgba(255,255,255,0.8)',
                                        }}
                                      >
                                        {isDeployingBackup ? <CircularProgress size={18} /> : <UploadIcon fontSize="small" />}
                                      </IconButton>
                                    </span>
                                  </Tooltip>
                                  <Tooltip title="Delete backup folders for this Edge Function">
                                    <span>
                                      <IconButton
                                        onClick={() => openDeleteBackupDialog(name)}
                                        disabled={deleteBackupLoading || deleteBackupRunning}
                                        size="small"
                                        color="error"
                                        sx={{
                                          border: '1px solid',
                                          borderColor: 'divider',
                                          bgcolor: 'rgba(255,255,255,0.8)',
                                        }}
                                      >
                                        <DeleteIcon fontSize="small" />
                                      </IconButton>
                                    </span>
                                  </Tooltip>
                                </>
                              ) : (
                              <Tooltip title="Upload/deploy this Edge Function">
                                <span>
                                  <IconButton
                                    onClick={() => openUploadDialogForFunction(name)}
                                    disabled={isBusy || isDeleting}
                                    size="small"
                                    sx={{
                                      border: '1px solid',
                                      borderColor: 'divider',
                                      bgcolor: 'rgba(255,255,255,0.8)',
                                    }}
                                  >
                                    <UploadIcon fontSize="small" />
                                  </IconButton>
                                </span>
                              </Tooltip>
                              )}
                              {edgeSource === 'supabase' ? (
                                <>
                                  <Tooltip title="Backup function source to storage/backup/edge-functions">
                                    <span>
                                      <IconButton
                                        onClick={() => backupEdgeFunction(name, 'copy')}
                                        disabled={isBusy || isDeleting}
                                        size="small"
                                        sx={{
                                          border: '1px solid',
                                          borderColor: 'divider',
                                          bgcolor: 'rgba(255,255,255,0.8)',
                                        }}
                                      >
                                        {isBusy ? <CircularProgress size={18} /> : <DownloadIcon fontSize="small" />}
                                      </IconButton>
                                    </span>
                                  </Tooltip>
                                  <Tooltip title="Delete this Edge Function from Supabase">
                                    <span>
                                      <IconButton
                                        onClick={() => openDeleteDialog(name)}
                                        disabled={isBusy || isDeleting}
                                        size="small"
                                        color="error"
                                        sx={{
                                          border: '1px solid',
                                          borderColor: 'divider',
                                          bgcolor: 'rgba(255,255,255,0.8)',
                                        }}
                                      >
                                        {isDeleting ? <CircularProgress size={18} /> : <DeleteIcon fontSize="small" />}
                                      </IconButton>
                                    </span>
                                  </Tooltip>
                                </>
                              ) : null}
                              <Chip
                                size="small"
                                label={edgeSource === 'backups' ? 'Backup' : 'Edge'}
                                color={edgeSource === 'backups' ? (status === 'ok' ? 'success' : 'warning') : 'info'}
                                sx={{ fontWeight: 800 }}
                              />
                            </Box>
                          </Box>
                        );
                      });
                    })()}
                  </Box>
                </CardContent>
              </Card>
            </Box>

            <Card sx={{ mt: 2, borderRadius: 3, overflow: 'hidden', border: '1px solid', borderColor: 'divider', boxShadow: 'none' }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <FolderIcon fontSize="small" />
                    <Typography fontWeight={900}>Storage Buckets</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={backupAllBuckets}
                      disabled={bucketBackupAllRunning || !!overview?.bucketsError}
                      startIcon={bucketBackupAllRunning ? <CircularProgress size={16} /> : <DownloadIcon />}
                      sx={{ textTransform: 'none', borderRadius: 999, fontWeight: 900 }}
                    >
                      Download all
                    </Button>
                    {overview?.bucketsError ? (
                      <Chip color="warning" label="Needs Postgres admin connection" sx={{ fontWeight: 800 }} />
                    ) : (
                      <Chip color="success" label="Connected" sx={{ fontWeight: 800 }} />
                    )}
                  </Box>
                </Box>

                {!!overview?.bucketsError && (
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    {String(overview.bucketsError)}
                  </Typography>
                )}

                <TableContainer sx={{ mt: 2, borderRadius: 2, border: '1px solid', borderColor: 'divider', maxHeight: 360, overflowY: 'auto' }}>
                  <Table stickyHeader size="small" sx={{ minWidth: 760 }}>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 900 }}>Name</TableCell>
                        <TableCell sx={{ fontWeight: 900 }}>Id</TableCell>
                        <TableCell sx={{ fontWeight: 900 }}>Access</TableCell>
                        <TableCell sx={{ fontWeight: 900 }}>Updated</TableCell>
                        <TableCell sx={{ fontWeight: 900 }} align="right">Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {(() => {
                        const q = (overviewQuery || '').trim().toLowerCase();
                        const buckets = Array.isArray(overview?.buckets) ? overview!.buckets! : [];
                        const filtered = q
                          ? buckets.filter((b) =>
                              `${b.name} ${b.id}`.toLowerCase().includes(q)
                            )
                          : buckets;

                        if (filtered.length === 0) {
                          return (
                            <TableRow>
                              <TableCell colSpan={5}>
                                <Typography variant="body2" color="text.secondary">
                                  {overview?.bucketsError
                                    ? 'Configure the Server-side Postgres connection to list storage buckets.'
                                    : 'No buckets found.'}
                                </Typography>
                              </TableCell>
                            </TableRow>
                          );
                        }

                        return filtered.slice(0, 200).map((b, idx) => {
                          const name = String(b?.name || '');
                          const id = String(b?.id || '');
                          const isPublic = !!b?.public;
                          const updated = String(b?.updated_at || b?.created_at || '');
                          const busyKey = String(id || name);
                          const isZipBusy = !!bucketZipBusy[busyKey];
                          const isSqlBusy = !!bucketSqlBusy[busyKey];
                          return (
                            <TableRow key={`${id}.${name}.${idx}`} hover>
                              <TableCell sx={{ fontFamily: 'monospace' }}>{name}</TableCell>
                              <TableCell sx={{ fontFamily: 'monospace', color: 'text.secondary' }}>{id}</TableCell>
                              <TableCell>
                                <Chip
                                  size="small"
                                  label={isPublic ? 'public' : 'private'}
                                  color={isPublic ? 'success' : 'default'}
                                  sx={{ fontWeight: 800 }}
                                />
                              </TableCell>
                              <TableCell sx={{ color: 'text.secondary' }}>
                                {updated ? new Date(updated).toLocaleString() : '-'}
                              </TableCell>
                              <TableCell align="right">
                                <Box sx={{ display: 'flex', gap: 0.75, justifyContent: 'flex-end' }}>
                                  <Tooltip title="Download as zip (fallback: .tar.gz)">
                                    <span>
                                      <IconButton
                                        size="small"
                                        disabled={isZipBusy || !!overview?.bucketsError}
                                        onClick={() => downloadBucketZip(id, name)}
                                        sx={{ border: '1px solid', borderColor: 'divider', bgcolor: 'rgba(255,255,255,0.8)' }}
                                      >
                                        {isZipBusy ? <CircularProgress size={18} /> : <DownloadIcon fontSize="small" />}
                                      </IconButton>
                                    </span>
                                  </Tooltip>
                                  <Tooltip title="Download bucket SQL">
                                    <span>
                                      <IconButton
                                        size="small"
                                        disabled={isSqlBusy || !!overview?.bucketsError}
                                        onClick={() => downloadBucketSql(id, name)}
                                        sx={{ border: '1px solid', borderColor: 'divider', bgcolor: 'rgba(255,255,255,0.8)' }}
                                      >
                                        {isSqlBusy ? <CircularProgress size={18} /> : <FolderIcon fontSize="small" />}
                                      </IconButton>
                                    </span>
                                  </Tooltip>
                                  <Tooltip title="Copy bucket name">
                                    <IconButton
                                      size="small"
                                      onClick={async () => {
                                        const ok = await copyToClipboard(name);
                                        if (ok) toast.success('Copied');
                                        else toast.error('Copy failed');
                                      }}
                                      sx={{ border: '1px solid', borderColor: 'divider', bgcolor: 'rgba(255,255,255,0.8)' }}
                                    >
                                      <ContentCopyIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                  <Tooltip title="Copy bucket id">
                                    <IconButton
                                      size="small"
                                      onClick={async () => {
                                        const ok = await copyToClipboard(id);
                                        if (ok) toast.success('Copied');
                                        else toast.error('Copy failed');
                                      }}
                                      sx={{ border: '1px solid', borderColor: 'divider', bgcolor: 'rgba(255,255,255,0.8)' }}
                                    >
                                      <ContentCopyIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                </Box>
                              </TableCell>
                            </TableRow>
                          );
                        });
                      })()}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          </Box>
        </CardContent>
      </Card>

      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 900 }}>Delete Edge Function</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            This will permanently delete the function from Supabase.
          </Typography>
          <Box sx={{ p: 1.25, borderRadius: 2, border: '1px solid', borderColor: 'divider', bgcolor: 'rgba(239,68,68,0.05)', mb: 2 }}>
            <Typography variant="caption" color="text.secondary">Target</Typography>
            <Typography fontWeight={900} sx={{ fontFamily: 'monospace' }}>{deleteTarget || '-'}</Typography>
          </Box>

          <FormControlLabel
            control={(
              <Switch
                checked={deleteBackupFirst}
                onChange={(e) => setDeleteBackupFirst(e.target.checked)}
              />
            )}
            label="Backup before delete"
          />

          <TextField
            label="Type the function name to confirm"
            value={deleteConfirm}
            onChange={(e) => setDeleteConfirm(e.target.value)}
            placeholder={deleteTarget}
            fullWidth
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} sx={{ textTransform: 'none' }}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            onClick={() => deleteEdgeFunction(deleteTarget, deleteBackupFirst)}
            disabled={!deleteTarget || deleteConfirm.trim() !== deleteTarget}
            sx={{ textTransform: 'none' }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={bulkDeleteBackupsOpen} onClose={() => (bulkDeleteBackupsRunning ? null : setBulkDeleteBackupsOpen(false))} maxWidth="md" fullWidth>
        <DialogTitle sx={{ fontWeight: 900 }}>Delete Edge Function backups</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Select backup folders to delete from <code>storage/backup/edge-functions</code>.
          </Typography>

          {bulkDeleteBackupsLoading ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'text.secondary' }}>
              <CircularProgress size={18} />
              <Typography variant="body2" color="text.secondary">Loading folders…</Typography>
            </Box>
          ) : bulkDeleteBackupsData.length === 0 ? (
            <Typography variant="body2" color="text.secondary">No backups found.</Typography>
          ) : (
            <>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1 }}>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => {
                    const next: Record<string, boolean> = {};
                    bulkDeleteBackupsData.forEach((fn) => {
                      fn.variants.forEach((v) => {
                        next[v.folder] = true;
                      });
                    });
                    setBulkDeleteBackupsSelected(next);
                  }}
                  sx={{ textTransform: 'none' }}
                >
                  Select all
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => {
                    const next: Record<string, boolean> = {};
                    bulkDeleteBackupsData.forEach((fn) => {
                      fn.variants.forEach((v) => {
                        next[v.folder] = false;
                      });
                    });
                    setBulkDeleteBackupsSelected(next);
                  }}
                  sx={{ textTransform: 'none' }}
                >
                  Clear
                </Button>
              </Box>

              <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, maxHeight: 420, overflowY: 'auto' }}>
                {bulkDeleteBackupsData.map((fn) => (
                  <Box key={fn.name} sx={{ p: 1.5, borderBottom: '1px solid', borderBottomColor: 'divider' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, mb: 1 }}>
                      <Typography fontWeight={900} sx={{ fontFamily: 'monospace' }}>{fn.name}</Typography>
                      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={() => {
                            setBulkDeleteBackupsSelected((p) => {
                              const next = { ...(p || {}) };
                              fn.variants.forEach((v) => {
                                next[v.folder] = true;
                              });
                              return next;
                            });
                          }}
                          sx={{ textTransform: 'none' }}
                        >
                          Select
                        </Button>
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={() => {
                            setBulkDeleteBackupsSelected((p) => {
                              const next = { ...(p || {}) };
                              fn.variants.forEach((v) => {
                                next[v.folder] = false;
                              });
                              return next;
                            });
                          }}
                          sx={{ textTransform: 'none' }}
                        >
                          Clear
                        </Button>
                      </Box>
                    </Box>

                    <Box sx={{ display: 'grid', gap: 1 }}>
                      {fn.variants.map((v) => (
                        <Box
                          key={v.folder}
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            gap: 2,
                            p: 1.25,
                            borderRadius: 2,
                            border: '1px solid',
                            borderColor: 'divider',
                            bgcolor: 'rgba(255,255,255,0.6)',
                          }}
                        >
                          <Box>
                            <Typography fontWeight={800} sx={{ fontFamily: 'monospace' }}>{v.folder}</Typography>
                            {v.updatedAt ? (
                              <Typography variant="caption" color="text.secondary">Updated {new Date(v.updatedAt).toLocaleString()}</Typography>
                            ) : null}
                          </Box>
                          <FormControlLabel
                            control={(
                              <Switch
                                checked={!!bulkDeleteBackupsSelected[v.folder]}
                                onChange={(e) => setBulkDeleteBackupsSelected((p) => ({ ...(p || {}), [v.folder]: e.target.checked }))}
                              />
                            )}
                            label=""
                          />
                        </Box>
                      ))}
                    </Box>
                  </Box>
                ))}
              </Box>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBulkDeleteBackupsOpen(false)} disabled={bulkDeleteBackupsRunning} sx={{ textTransform: 'none' }}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            onClick={runBulkDeleteBackups}
            disabled={bulkDeleteBackupsLoading || bulkDeleteBackupsRunning}
            startIcon={bulkDeleteBackupsRunning ? <CircularProgress size={18} /> : <DeleteIcon />}
            sx={{ textTransform: 'none' }}
          >
            Delete selected
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={deleteBackupDialogOpen} onClose={() => (deleteBackupRunning ? null : setDeleteBackupDialogOpen(false))} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 900 }}>Delete backup folders</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Select which local backup folders you want to delete from <code>storage/backup/edge-functions</code>.
          </Typography>
          <Box sx={{ p: 1.25, borderRadius: 2, border: '1px solid', borderColor: 'divider', bgcolor: 'rgba(239,68,68,0.05)', mb: 2 }}>
            <Typography variant="caption" color="text.secondary">Function</Typography>
            <Typography fontWeight={900} sx={{ fontFamily: 'monospace' }}>{deleteBackupTarget || '-'}</Typography>
          </Box>

          {deleteBackupLoading ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'text.secondary' }}>
              <CircularProgress size={18} />
              <Typography variant="body2" color="text.secondary">Loading folders…</Typography>
            </Box>
          ) : deleteBackupVariants.length === 0 ? (
            <Typography variant="body2" color="text.secondary">No backup folders found.</Typography>
          ) : (
            <Box sx={{ display: 'grid', gap: 1 }}>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => {
                    const next: Record<string, boolean> = {};
                    deleteBackupVariants.forEach((v) => {
                      next[v.folder] = true;
                    });
                    setDeleteBackupSelected(next);
                  }}
                  sx={{ textTransform: 'none' }}
                >
                  Select all
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => {
                    const next: Record<string, boolean> = {};
                    deleteBackupVariants.forEach((v) => {
                      next[v.folder] = false;
                    });
                    setDeleteBackupSelected(next);
                  }}
                  sx={{ textTransform: 'none' }}
                >
                  Clear
                </Button>
              </Box>

              {deleteBackupVariants.map((v) => (
                <Box
                  key={v.folder}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 2,
                    p: 1.25,
                    borderRadius: 2,
                    border: '1px solid',
                    borderColor: 'divider',
                    bgcolor: 'rgba(255,255,255,0.6)',
                  }}
                >
                  <Box>
                    <Typography fontWeight={800} sx={{ fontFamily: 'monospace' }}>{v.folder}</Typography>
                    {v.updatedAt ? (
                      <Typography variant="caption" color="text.secondary">Updated {new Date(v.updatedAt).toLocaleString()}</Typography>
                    ) : null}
                  </Box>
                  <FormControlLabel
                    control={(
                      <Switch
                        checked={!!deleteBackupSelected[v.folder]}
                        onChange={(e) => setDeleteBackupSelected((p) => ({ ...p, [v.folder]: e.target.checked }))}
                      />
                    )}
                    label=""
                  />
                </Box>
              ))}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteBackupDialogOpen(false)} disabled={deleteBackupRunning} sx={{ textTransform: 'none' }}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            onClick={runDeleteBackupFolders}
            disabled={deleteBackupLoading || deleteBackupRunning}
            startIcon={deleteBackupRunning ? <CircularProgress size={18} /> : <DeleteIcon />}
            sx={{ textTransform: 'none' }}
          >
            Delete selected
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={applyAllOpen} onClose={() => setApplyAllOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 900 }}>Upload all table schemas</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            This will apply the latest <code>schema.sql</code> found for each table in <code>storage/backup/tables</code>.
          </Typography>
          <Box sx={{ p: 1.5, borderRadius: 2, border: '1px solid', borderColor: 'divider', bgcolor: 'rgba(220,38,38,0.04)' }}>
            <Typography variant="body2" sx={{ fontWeight: 900, color: 'error.main' }}>Be careful</Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
              If a table already exists, it will be reported as skipped. Other SQL errors will be reported as failures.
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setApplyAllOpen(false)} sx={{ textTransform: 'none' }}>Cancel</Button>
          <Button variant="contained" onClick={applyAllSchemas} sx={{ textTransform: 'none' }}>Apply all</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={applyAllProgressOpen} onClose={() => (applyAllRunning ? null : setApplyAllProgressOpen(false))} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 900 }}>Uploading schemas</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            {applyAllTotal > 0 ? `${applyAllDone} / ${applyAllTotal} completed` : 'Working...'}
          </Typography>
          <Box sx={{ mt: 2, height: 10, borderRadius: 999, bgcolor: 'rgba(2,132,199,0.10)', overflow: 'hidden' }}>
            <Box
              sx={{
                height: '100%',
                width: applyAllTotal > 0 ? `${Math.round((applyAllDone / applyAllTotal) * 100)}%` : '0%',
                bgcolor: 'primary.main',
                transition: 'width 180ms ease',
              }}
            />
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
            <Typography variant="caption" color="text.secondary">
              {applyAllTotal > 0 ? `${Math.round((applyAllDone / applyAllTotal) * 100)}%` : '0%'}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Applied: {applyAllApplied} · Skipped: {applyAllSkipped} · Failed: {applyAllFailed}
            </Typography>
          </Box>
          {applyAllCurrent ? (
            <Box sx={{ mt: 2, p: 1.25, borderRadius: 2, border: '1px solid', borderColor: 'divider', bgcolor: 'rgba(2,132,199,0.03)' }}>
              <Typography variant="caption" color="text.secondary">Current</Typography>
              <Typography fontWeight={800} sx={{ fontFamily: 'monospace' }}>{applyAllCurrent}</Typography>
            </Box>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setApplyAllProgressOpen(false)} disabled={applyAllRunning} sx={{ textTransform: 'none' }}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={applyAllResultsOpen} onClose={() => setApplyAllResultsOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ fontWeight: 900 }}>Upload results</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Applied: {applyAllApplied} · Skipped (already exists): {applyAllSkipped} · Failed: {applyAllFailed}
          </Typography>

          <TableContainer sx={{ borderRadius: 2, border: '1px solid', borderColor: 'divider', maxHeight: 420 }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 900 }}>Table</TableCell>
                  <TableCell sx={{ fontWeight: 900 }}>Status</TableCell>
                  <TableCell sx={{ fontWeight: 900 }}>Details</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {applyAllResults.map((r, idx) => (
                  <TableRow key={`${r.name}.${idx}`} hover>
                    <TableCell sx={{ fontFamily: 'monospace', whiteSpace: 'nowrap' }}>{r.name}</TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={r.status}
                        color={r.status === 'applied' ? 'success' : r.status === 'skipped_exists' ? 'warning' : 'error'}
                        sx={{ fontWeight: 800 }}
                      />
                    </TableCell>
                    <TableCell>
                      {r.folderName ? (
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                          Backup: {r.folderName}
                        </Typography>
                      ) : null}
                      {r.message ? (
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                          {r.message}
                        </Typography>
                      ) : null}
                      {r.error ? (
                        <Typography variant="caption" sx={{ display: 'block', whiteSpace: 'pre-wrap' }}>
                          {r.error}
                        </Typography>
                      ) : null}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setApplyAllResultsOpen(false)} sx={{ textTransform: 'none' }}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={tableUploadOpen} onClose={() => (tableUploadApplying ? null : setTableUploadOpen(false))} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 900 }}>
          Upload table schema
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            This will run the selected <code>schema.sql</code> against your configured server-side Postgres connection.
          </Typography>

          <Typography variant="body2" sx={{ fontWeight: 900, mb: 1 }}>
            Target
          </Typography>
          <Typography variant="body2" sx={{ fontFamily: 'monospace', mb: 2 }}>
            {tableUploadTarget ? `${tableUploadTarget.schema}.${tableUploadTarget.table}` : '-'}
          </Typography>

          {tableUploadLoading ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CircularProgress size={18} />
              <Typography variant="body2" color="text.secondary">Loading backups...</Typography>
            </Box>
          ) : tableUploadBackups.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No backups found in <code>storage/backup/tables/{tableUploadTarget?.schema}/{tableUploadTarget?.table}</code>.
            </Typography>
          ) : (
            <TextField
              select
              label="Select a backup"
              fullWidth
              value={tableUploadSelected}
              onChange={(e) => setTableUploadSelected(e.target.value)}
              helperText="Choose which schema.sql to apply"
            >
              {tableUploadBackups.map((b) => (
                <MenuItem key={b.folderName} value={b.folderName}>
                  {b.folderName}{b.updatedAt ? ` (updated ${b.updatedAt})` : ''}
                </MenuItem>
              ))}
            </TextField>
          )}

          <Box sx={{ mt: 2, p: 1.5, borderRadius: 2, border: '1px solid', borderColor: 'divider', bgcolor: 'rgba(220,38,38,0.04)' }}>
            <Typography variant="body2" sx={{ fontWeight: 900, color: 'error.main' }}>Be careful</Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
              This can modify your database. If the table already exists, some statements may fail depending on your schema.sql.
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTableUploadOpen(false)} disabled={tableUploadApplying} sx={{ textTransform: 'none' }}>Cancel</Button>
          <Button
            variant="contained"
            onClick={applyTableSchemaBackup}
            disabled={tableUploadApplying || tableUploadLoading || !tableUploadSelected || !tableUploadTarget}
            sx={{ textTransform: 'none' }}
          >
            {tableUploadApplying ? 'Applying...' : 'Apply schema'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={tableBulkDialogOpen} onClose={() => setTableBulkDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 900 }}>Download all tables</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            This will export each table as SQL DDL into <code>storage/backup/tables/&lt;schema&gt;/&lt;table&gt;/schema.sql</code>.
          </Typography>

          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Includes: columns, constraints, indexes, triggers, rules, and RLS policies.
          </Typography>

          <Typography fontWeight={900} sx={{ mb: 1 }}>When a backup already exists</Typography>
          <Box sx={{ display: 'grid', gap: 1 }}>
            <Button
              variant={tableBulkMode === 'copy' ? 'contained' : 'outlined'}
              onClick={() => setTableBulkMode('copy')}
              sx={{ textTransform: 'none', justifyContent: 'space-between' }}
            >
              Make copies
              <Typography variant="caption" color="text.secondary">(table_2, table_3...)</Typography>
            </Button>
            <Button
              variant={tableBulkMode === 'overwrite' ? 'contained' : 'outlined'}
              onClick={() => setTableBulkMode('overwrite')}
              sx={{ textTransform: 'none', justifyContent: 'space-between' }}
            >
              Replace existing
              <Typography variant="caption" color="text.secondary">(overwrite files)</Typography>
            </Button>
          </Box>

          <TextField
            label="Export type"
            value="SQL schema (DDL)"
            fullWidth
            sx={{ mt: 2 }}
            helperText="This generates schema.sql for each table. (Data export can be added separately if needed.)"
            InputProps={{ readOnly: true }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTableBulkDialogOpen(false)} sx={{ textTransform: 'none' }}>Cancel</Button>
          <Button variant="contained" onClick={startTableBulkBackup} sx={{ textTransform: 'none' }}>Start download</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={tableBulkProgressOpen} onClose={() => (tableBulkRunning ? null : setTableBulkProgressOpen(false))} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 900 }}>Downloading tables</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            {tableBulkTotal > 0 ? `${tableBulkDone} / ${tableBulkTotal} completed` : 'Preparing...'}
          </Typography>
          <Box sx={{ mt: 2, height: 10, borderRadius: 999, bgcolor: 'rgba(2,132,199,0.10)', overflow: 'hidden' }}>
            <Box
              sx={{
                height: '100%',
                width: tableBulkTotal > 0 ? `${Math.round((tableBulkDone / tableBulkTotal) * 100)}%` : '0%',
                bgcolor: 'primary.main',
                transition: 'width 180ms ease',
              }}
            />
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
            <Typography variant="caption" color="text.secondary">
              {tableBulkTotal > 0 ? `${Math.round((tableBulkDone / tableBulkTotal) * 100)}%` : '0%'}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Failed: {tableBulkFailed} · Mode: {tableBulkMode}
            </Typography>
          </Box>
          {tableBulkCurrent ? (
            <Box sx={{ mt: 2, p: 1.25, borderRadius: 2, border: '1px solid', borderColor: 'divider', bgcolor: 'rgba(2,132,199,0.03)' }}>
              <Typography variant="caption" color="text.secondary">Current</Typography>
              <Typography fontWeight={800} sx={{ fontFamily: 'monospace' }}>{tableBulkCurrent}</Typography>
            </Box>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTableBulkProgressOpen(false)} disabled={tableBulkRunning} sx={{ textTransform: 'none' }}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={tableBulkFailureDialogOpen} onClose={() => setTableBulkFailureDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ fontWeight: 900 }}>Tables that failed</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            These tables could not be exported.
          </Typography>

          <TableContainer sx={{ borderRadius: 2, border: '1px solid', borderColor: 'divider', maxHeight: 360 }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 900 }}>Table</TableCell>
                  <TableCell sx={{ fontWeight: 900 }}>Error</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {tableBulkFailures.map((f, idx) => (
                  <TableRow key={`${f.name}.${idx}`} hover>
                    <TableCell sx={{ fontFamily: 'monospace', whiteSpace: 'nowrap' }}>{f.name}</TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 700 }}>{f.error}</Typography>
                      {f.details ? (
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', whiteSpace: 'pre-wrap', mt: 0.5 }}>
                          {f.details}
                        </Typography>
                      ) : null}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTableBulkFailureDialogOpen(false)} sx={{ textTransform: 'none' }}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={bulkUploadFailureDialogOpen} onClose={() => setBulkUploadFailureDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ fontWeight: 900 }}>Uploads that failed</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            These functions could not be deployed from backups. The message below is returned by the Supabase Management API.
          </Typography>

          <TableContainer sx={{ borderRadius: 2, border: '1px solid', borderColor: 'divider', maxHeight: 360 }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 900 }}>Function</TableCell>
                  <TableCell sx={{ fontWeight: 900 }}>Error</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {bulkUploadFailures.map((f, idx) => (
                  <TableRow key={`${f.name}.${idx}`} hover>
                    <TableCell sx={{ fontFamily: 'monospace', whiteSpace: 'nowrap' }}>{f.name}</TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 700 }}>{f.error}</Typography>
                      {f.details ? (
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ display: 'block', whiteSpace: 'pre-wrap', mt: 0.5 }}
                        >
                          {f.details}
                        </Typography>
                      ) : null}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBulkUploadFailureDialogOpen(false)} sx={{ textTransform: 'none' }}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={bulkUploadDialogOpen} onClose={() => (bulkUploadRunning ? null : setBulkUploadDialogOpen(false))} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 900 }}>Upload missing Edge Functions</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            This will deploy Edge Functions from <code>storage/backup/edge-functions</code> that are not currently present in Supabase.
          </Typography>
          <Box sx={{ p: 1.25, borderRadius: 2, border: '1px solid', borderColor: 'divider', bgcolor: 'rgba(2,132,199,0.03)' }}>
            <Typography variant="caption" color="text.secondary">Detected missing</Typography>
            <Typography fontWeight={900} sx={{ fontFamily: 'monospace' }}>{bulkUploadCandidates.length}</Typography>
          </Box>
          {bulkUploadCandidates.length > 0 ? (
            <Box sx={{ mt: 2, display: 'grid', gap: 0.75, maxHeight: 240, overflowY: 'auto', pr: 0.5 }}>
              {bulkUploadCandidates.slice(0, 50).map((x) => (
                <Typography key={x.name} sx={{ fontFamily: 'monospace', fontWeight: 800 }}>
                  {x.name}
                </Typography>
              ))}
            </Box>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBulkUploadDialogOpen(false)} disabled={bulkUploadRunning} sx={{ textTransform: 'none' }}>Cancel</Button>
          <Button variant="contained" onClick={startBulkUpload} disabled={bulkUploadRunning || bulkUploadCandidates.length === 0} sx={{ textTransform: 'none' }}>
            Start upload
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={bulkUploadProgressOpen} onClose={() => (bulkUploadRunning ? null : setBulkUploadProgressOpen(false))} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 900 }}>Uploading Edge Functions</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            {bulkUploadTotal > 0 ? `${bulkUploadDone} / ${bulkUploadTotal} completed` : 'Preparing...'}
          </Typography>
          <Box sx={{ mt: 2, height: 10, borderRadius: 999, bgcolor: 'rgba(2,132,199,0.10)', overflow: 'hidden' }}>
            <Box
              sx={{
                height: '100%',
                width: bulkUploadTotal > 0 ? `${Math.round((bulkUploadDone / bulkUploadTotal) * 100)}%` : '0%',
                bgcolor: 'primary.main',
                transition: 'width 180ms ease',
              }}
            />
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
            <Typography variant="caption" color="text.secondary">
              {bulkUploadTotal > 0 ? `${Math.round((bulkUploadDone / bulkUploadTotal) * 100)}%` : '0%'}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Failed: {bulkUploadFailed}
            </Typography>
          </Box>
          {bulkUploadCurrent ? (
            <Box sx={{ mt: 2, p: 1.25, borderRadius: 2, border: '1px solid', borderColor: 'divider', bgcolor: 'rgba(2,132,199,0.03)' }}>
              <Typography variant="caption" color="text.secondary">Current</Typography>
              <Typography fontWeight={800} sx={{ fontFamily: 'monospace' }}>{bulkUploadCurrent}</Typography>
            </Box>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBulkUploadProgressOpen(false)} disabled={bulkUploadRunning} sx={{ textTransform: 'none' }}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={uploadDialogOpen} onClose={() => (uploadBusy ? null : setUploadDialogOpen(false))} maxWidth="md" fullWidth>
        <DialogTitle sx={{ fontWeight: 900, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <UploadIcon fontSize="small" />
            Deploy Edge Function
          </Box>
          {uploadBusy ? <CircularProgress size={18} /> : null}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Upload a <code>.ts</code> file or paste code. This deploys the function to Supabase via the Management API.
          </Typography>

          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2, mb: 2 }}>
            <TextField
              label="Function name"
              value={uploadName}
              onChange={(e) => setUploadName(e.target.value)}
              placeholder="my-function"
              fullWidth
              disabled={uploadBusy}
              helperText="Letters, numbers, underscore, hyphen. Must start with a letter."
            />
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <input
                  ref={uploadFileRef}
                  type="file"
                  accept=".ts"
                  style={{ display: 'none' }}
                  onChange={onUploadFileChange}
                />
                <Button
                  variant={uploadMode === 'file' ? 'contained' : 'outlined'}
                  onClick={onPickUploadFile}
                  disabled={uploadBusy}
                  sx={{ textTransform: 'none' }}
                >
                  Choose .ts file
                </Button>
                <Button
                  variant={uploadMode === 'editor' ? 'contained' : 'outlined'}
                  onClick={() => setUploadMode('editor')}
                  disabled={uploadBusy}
                  sx={{ textTransform: 'none' }}
                >
                  Editor
                </Button>
              </Box>
            </Box>
          </Box>

          <TextField
            label={uploadMode === 'file' ? 'Code (from file)' : 'Code'}
            value={uploadCode}
            onChange={(e) => setUploadCode(e.target.value)}
            fullWidth
            multiline
            minRows={14}
            disabled={uploadBusy}
            placeholder={'import { serve } from "https://deno.land/std/http/server.ts"\n\nserve(() => new Response("Hello"))\n'}
            sx={{
              '& textarea': {
                fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
                fontSize: 13,
                lineHeight: 1.5,
              },
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUploadDialogOpen(false)} disabled={uploadBusy} sx={{ textTransform: 'none' }}>Cancel</Button>
          <Button variant="contained" onClick={deployEdgeFunction} disabled={uploadBusy} sx={{ textTransform: 'none' }}>
            Deploy
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={edgeHelpOpen} onClose={() => setEdgeHelpOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 900 }}>Enable Edge Functions listing</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            To list Edge Functions, the server needs Supabase Management API access. You’ll save a Project ref + a Personal Access Token
            (PAT) in the “Management API (optional)” section above. The token is stored server-side and is write-only.
          </Typography>

          <Box sx={{ display: 'grid', gap: 1.25 }}>
            <Typography variant="body2"><b>Step 1:</b> Find your Project ref</Typography>
            <Typography variant="body2" color="text.secondary">
              It’s the <b>subdomain</b> in your URL: <code>https://&lt;ref&gt;.supabase.co</code>
            </Typography>
            <Typography variant="body2">
              <Link
                href="https://supabase.com/dashboard"
                target="_blank"
                rel="noreferrer"
                underline="hover"
              >
                Open Supabase dashboard
              </Link>
            </Typography>

            <Typography variant="body2" sx={{ mt: 1 }}><b>Step 2:</b> Create a Personal Access Token (PAT)</Typography>
            <Typography variant="body2" color="text.secondary">
              Create a token under your account settings.
            </Typography>
            <Typography variant="body2">
              <Link
                href="https://supabase.com/dashboard/account/tokens"
                target="_blank"
                rel="noreferrer"
                underline="hover"
              >
                Go directly to Access Tokens
              </Link>
            </Typography>

            <Typography variant="body2" sx={{ mt: 1 }}><b>Step 3:</b> Save it in this Admin panel</Typography>
            <Typography variant="body2" color="text.secondary">
              Paste the Project ref + PAT, click <b>Save</b>, then click <b>Refresh overview</b>.
            </Typography>

            <Typography variant="body2" sx={{ mt: 1 }}><b>If it still shows empty:</b></Typography>
            <Typography variant="body2" color="text.secondary">
              Make sure the token is valid and the Project ref matches your project. If the server can’t reach the Management API,
              you’ll see an error message under “Edge Functions”.
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEdgeHelpOpen(false)} sx={{ textTransform: 'none' }}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={bulkFailureDialogOpen} onClose={() => setBulkFailureDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ fontWeight: 900 }}>Edge Functions that failed</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            These functions could not be backed up. If the reason mentions Supabase CLI, install it and ensure it is available to PHP.
          </Typography>

          <TableContainer sx={{ borderRadius: 2, border: '1px solid', borderColor: 'divider', maxHeight: 360 }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 900 }}>Function</TableCell>
                  <TableCell sx={{ fontWeight: 900 }}>Error</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {bulkFailures.map((f, idx) => (
                  <TableRow key={`${f.name}.${idx}`} hover>
                    <TableCell sx={{ fontFamily: 'monospace', whiteSpace: 'nowrap' }}>{f.name}</TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 700 }}>{f.error}</Typography>
                      {f.details ? (
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ display: 'block', whiteSpace: 'pre-wrap', mt: 0.5 }}
                        >
                          {f.details}
                        </Typography>
                      ) : null}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBulkFailureDialogOpen(false)} sx={{ textTransform: 'none' }}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={bulkDialogOpen} onClose={() => setBulkDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 900 }}>Download all Edge Functions</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            This will save each Edge Function into <code>storage/backup/edge-functions/</code>.
          </Typography>

          <Typography fontWeight={900} sx={{ mb: 1 }}>When a backup already exists</Typography>
          <Box sx={{ display: 'grid', gap: 1 }}>
            <Button
              variant={bulkMode === 'copy' ? 'contained' : 'outlined'}
              onClick={() => setBulkMode('copy')}
              sx={{ textTransform: 'none', justifyContent: 'space-between' }}
            >
              <span>Make copies</span>
              <span style={{ opacity: 0.8 }}>Creates folders like <code>name_2</code>, <code>name_3</code>…</span>
            </Button>
            <Button
              variant={bulkMode === 'overwrite' ? 'contained' : 'outlined'}
              onClick={() => setBulkMode('overwrite')}
              sx={{ textTransform: 'none', justifyContent: 'space-between' }}
            >
              <span>Replace existing</span>
              <span style={{ opacity: 0.8 }}>Overwrites <code>name/index.ts</code></span>
            </Button>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBulkDialogOpen(false)} sx={{ textTransform: 'none' }}>Cancel</Button>
          <Button variant="contained" onClick={startBulkBackup} sx={{ textTransform: 'none' }}>Start download</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={bulkProgressOpen} onClose={() => (bulkRunning ? null : setBulkProgressOpen(false))} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 900 }}>Downloading Edge Functions</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            {bulkTotal > 0 ? `${bulkDone} / ${bulkTotal} completed` : 'Preparing...'}
          </Typography>
          <Box sx={{ mt: 2, height: 10, borderRadius: 999, bgcolor: 'rgba(2,132,199,0.10)', overflow: 'hidden' }}>
            <Box
              sx={{
                height: '100%',
                width: bulkTotal > 0 ? `${Math.round((bulkDone / bulkTotal) * 100)}%` : '0%',
                bgcolor: 'primary.main',
                transition: 'width 180ms ease',
              }}
            />
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
            <Typography variant="caption" color="text.secondary">
              {bulkTotal > 0 ? `${Math.round((bulkDone / bulkTotal) * 100)}%` : '0%'}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Failed: {bulkFailed} · Skipped: {bulkSkipped} · Mode: {bulkMode}
            </Typography>
          </Box>

          {bulkCurrent ? (
            <Box sx={{ mt: 2, p: 1.25, borderRadius: 2, border: '1px solid', borderColor: 'divider', bgcolor: 'rgba(2,132,199,0.03)' }}>
              <Typography variant="caption" color="text.secondary">Current</Typography>
              <Typography fontWeight={800} sx={{ fontFamily: 'monospace' }}>{bulkCurrent}</Typography>
            </Box>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setBulkProgressOpen(false)}
            disabled={bulkRunning}
            sx={{ textTransform: 'none' }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={pgPasswordPromptOpen} onClose={() => setPgPasswordPromptOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Enter database password</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Your connection string did not include a password (or it used a placeholder). The password is stored server-side only.
          </Typography>
          <TextField
            autoFocus
            label="Database password"
            type="password"
            value={pgPasswordPromptValue}
            onChange={(e) => setPgPasswordPromptValue(e.target.value)}
            fullWidth
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPgPasswordPromptOpen(false)} sx={{ textTransform: 'none' }}>Cancel</Button>
          <Button
            variant="contained"
            onClick={async () => {
              const pending = pgPendingSave;
              const pw = (pgPasswordPromptValue || '').trim();
              if (!pending) {
                setPgPasswordPromptOpen(false);
                return;
              }
              if (!pw) {
                toast.error('Please enter a password');
                return;
              }
              const id = toast.loading('Saving Postgres config...');
              try {
                await savePostgresConfig({ ...pending, password: pw });
                setPgPassword('');
                setPgPasswordPromptOpen(false);
                setPgPendingSave(null);
                toast.success('Saved', { id });
              } catch (e: any) {
                toast.error(e?.message || 'Failed to save', { id });
              }
            }}
            sx={{ textTransform: 'none' }}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!pgHelp?.open} onClose={() => setPgHelp(null)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 900 }}>{pgHelp?.title || 'Connection help'}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {pgHelp?.message}
          </Typography>
          {Array.isArray(pgHelp?.steps) && pgHelp!.steps!.length > 0 && (
            <Box sx={{ display: 'grid', gap: 1.2 }}>
              {pgHelp!.steps!.map((s, i) => (
                <Box key={i} sx={{ display: 'flex', gap: 1 }}>
                  <Box sx={{ mt: '3px', width: 18, height: 18, borderRadius: 999, bgcolor: 'primary.main', color: 'primary.contrastText', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 900 }}>
                    {i + 1}
                  </Box>
                  <Typography variant="body2">{s}</Typography>
                </Box>
              ))}
            </Box>
          )}
          {!!pgHelp?.rawError && (
            <Box sx={{ mt: 2, p: 1.5, borderRadius: 2, bgcolor: 'rgba(2,132,199,0.06)', border: '1px solid', borderColor: 'divider' }}>
              <Typography variant="caption" color="text.secondary">Raw error</Typography>
              <Typography variant="body2" sx={{ fontFamily: 'monospace', mt: 0.5, whiteSpace: 'pre-wrap' }}>
                {pgHelp.rawError}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPgHelp(null)} sx={{ textTransform: 'none' }}>Close</Button>
        </DialogActions>
      </Dialog>

      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2, mb: 3 }}>
        <Box>
          <Typography variant="h4" fontWeight="bold" gutterBottom>
            Integrations & API Keys
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Manage provider API keys stored on the server. Keys are saved as JSON files per provider.
          </Typography>
        </Box>
        <Chip
          icon={<VpnKeyIcon />}
          color="info"
          label="Server storage"
          sx={{ fontWeight: 700 }}
        />
      </Box>

      <Card sx={{ mb: 3, borderRadius: 3, overflow: 'hidden' }}>
        <CardContent sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
          <FormControlLabel
            control={
              <Switch
                checked={Object.values(showSecrets).some(Boolean)}
                onChange={(e) => {
                  const next = !!e.target.checked;
                  const obj: Record<string, boolean> = {};
                  for (const p of PROVIDERS) obj[p.id] = next;
                  setShowSecrets(obj);
                }}
              />
            }
            label="Reveal keys"
          />
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Button
              variant="outlined"
              onClick={fetchAll}
              disabled={loading}
              sx={{ textTransform: 'none' }}
            >
              {loading ? <CircularProgress size={18} /> : 'Refresh'}
            </Button>
          </Box>
        </CardContent>
      </Card>

      {PROVIDERS.map((p) => {
        const rows = keysByProvider[p.id] || [];
        const reveal = !!showSecrets[p.id];
        return (
          <Accordion
            key={p.id}
            expanded={expanded === p.id}
            onChange={(_, isExp) => setExpanded(isExp ? p.id : false)}
            sx={{ mb: 2, borderRadius: 2, '&:before': { display: 'none' } }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', pr: 1 }}>
                <Box>
                  <Typography fontWeight={800}>{p.label}</Typography>
                  <Typography variant="body2" color="text.secondary">{p.description}</Typography>
                </Box>
                <Chip
                  size="small"
                  label={`${rows.length} key${rows.length === 1 ? '' : 's'}`}
                  color={rows.length > 0 ? 'success' : 'default'}
                  sx={{ fontWeight: 700 }}
                />
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, gap: 2, flexWrap: 'wrap' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={() => openCreate(p.id)}
                    sx={{ textTransform: 'none' }}
                  >
                    Add key
                  </Button>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={reveal}
                        onChange={(e) => setShowSecrets((prev) => ({ ...prev, [p.id]: !!e.target.checked }))}
                      />
                    }
                    label="Reveal"
                  />
                </Box>
              </Box>

              <TableContainer sx={{ borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 800 }}>Name</TableCell>
                      <TableCell sx={{ fontWeight: 800 }}>Key</TableCell>
                      <TableCell sx={{ fontWeight: 800, width: 190 }}>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {rows.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={3}>
                          <Typography color="text.secondary">
                            No keys yet. Click “Add key” to create one.
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      rows.map((r) => (
                        <TableRow key={r.id} hover>
                          <TableCell>
                            <Typography fontWeight={700}>{r.name}</Typography>
                            <Typography variant="caption" color="text.secondary">id: {r.id}</Typography>
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Typography sx={{ fontFamily: 'monospace' }}>
                                {reveal ? r.key : maskKey(r.key)}
                              </Typography>
                              <Tooltip title="Copy">
                                <IconButton
                                  size="small"
                                  onClick={async () => {
                                    const ok = await copyToClipboard(r.key || '');
                                    if (ok) toast.success('Copied');
                                    else toast.error('Copy failed');
                                  }}
                                >
                                  <ContentCopyIcon fontSize="inherit" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title={reveal ? 'Hide' : 'Reveal'}>
                                <IconButton
                                  size="small"
                                  onClick={() => setShowSecrets((prev) => ({ ...prev, [p.id]: !reveal }))}
                                >
                                  {reveal ? <VisibilityOffIcon fontSize="inherit" /> : <VisibilityIcon fontSize="inherit" />}
                                </IconButton>
                              </Tooltip>
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Button
                                variant="outlined"
                                size="small"
                                startIcon={<EditIcon />}
                                onClick={() => openEdit(p.id, r)}
                                sx={{ textTransform: 'none' }}
                              >
                                Edit
                              </Button>
                              <Button
                                variant="outlined"
                                color="error"
                                size="small"
                                startIcon={<DeleteIcon />}
                                disabled={deleting}
                                onClick={() => {
                                  const ok = window.confirm(`Delete key “${r.name}”?`);
                                  if (!ok) return;
                                  deleteKey(p.id, r.id);
                                }}
                                sx={{ textTransform: 'none' }}
                              >
                                Delete
                              </Button>
                            </Box>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </AccordionDetails>
          </Accordion>
        );
      })}

      <Dialog open={editOpen} onClose={closeEdit} fullWidth maxWidth="sm">
        <DialogTitle sx={{ fontWeight: 800 }}>
          {editId ? 'Edit API Key' : 'Add API Key'}
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Box sx={{ display: 'grid', gap: 2, mt: 1 }}>
            <TextField
              label="Provider"
              value={PROVIDERS.find((p) => p.id === editProvider)?.label || editProvider}
              disabled
            />
            <TextField
              label="Name"
              value={editName}
              onChange={(e) => setEditName(e.target.value)}
              placeholder="e.g. Production / Server / EU"
              fullWidth
            />
            <TextField
              label="API Key"
              value={editKey}
              onChange={(e) => setEditKey(e.target.value)}
              placeholder="Paste the key here"
              fullWidth
              multiline
              minRows={2}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={closeEdit} sx={{ textTransform: 'none' }}>
            Cancel
          </Button>
          <Button
            onClick={saveKey}
            variant="contained"
            disabled={saving}
            sx={{ textTransform: 'none' }}
          >
            {saving ? <CircularProgress size={18} /> : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default IntegrationsApiKeysTab;
